/**
 * English translations for documentation pages
 */

const en: Record<string, string> = {
  // Common
  'common.backToHome': 'Back to Home',
  'common.backToDocs': 'Back to Documentation',
  'common.learnMore': 'Learn More',
  'common.getStarted': 'Get Started',
  'common.viewDocs': 'View Documentation',
  'common.signIn': 'Sign In',
  'common.signUp': 'Sign Up',
  'common.dashboard': 'Dashboard',
  'common.footer': '2025 Kizuna AI Lab. All rights reserved.',
  'common.language': 'Language',

  // Theme
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',

  // Navigation
  'nav.home': 'Home',
  'nav.docs': 'Documentation',
  'nav.scenarios': 'Scenarios',
  'nav.features': 'Features',
  'nav.signIn': 'Sign in',
  'nav.providers': 'Providers',
  'nav.docsShort': 'Docs',
  'footer.tagline': 'By Kizuna AI Lab. Breaking barriers, building connections.',
  'footer.trust': 'free and open source · on-device ai included · no subscription',
  'footer.product': 'Product',
  'footer.install': 'Install',
  'footer.open': 'Open',
  'footer.byok': 'Bring your own key',
  'footer.extension': 'Extension',
  'footer.gettingStarted': 'Getting started',
  'footer.meetingSites': 'Meeting sites',
  'footer.providerSetup': 'Provider setup',
  'footer.subtitleMode': 'Subtitle mode',
  'nav.localAi': 'Local AI',
  'nav.download': 'Download',
  'demo.me': 'Me',
  'demo.other': 'Other',
  'demo.both': 'Both',
  'demo.src': 'Src',
  'demo.trans': 'Trans',
  'demo.subtitle': 'Subtitle',
  'demo.exitSubtitle': 'Exit subtitle mode',
  'demo.settings': 'Settings',
  'demo.channel.mic': 'Your microphone',
  'demo.channel.sys': 'The other side',
  'demo.channel.out': 'What Sokuji speaks',
  'demo.endSession': 'End session',
  'home.scenarios.title': 'What are you trying to do?',
  'home.scenarios.lead': 'Sokuji is built so the cost of crossing a language falls on the person who wants to be understood. That is what we built first. But you are not always that person — sometimes you are in a lecture, listening to a recording, or on a call somebody else set up.',
  'home.scenarios.featured': 'the one we built first',
  'home.scenarios.more': 'What each one sets',
  'home.install.title': 'Install the app, or add it to your browser',
  'home.install.lead': 'The desktop app is not tied to a list of supported websites. It registers as a microphone on your machine, so anything that can choose a microphone can use it.',
  'home.install.desktop.title': 'Desktop app — system level',
  'home.install.desktop.body': 'Sokuji appears as an ordinary microphone in your operating system. Meeting apps, browsers, recorders, streaming software, games — if it lets you pick a mic, it can hear your translated voice.',
  'home.install.desktop.note': 'Coming the other way it asks permission of nobody: Sokuji reads whatever your machine is playing, system audio included, so a video, a stream or a recording is subtitled the same way a call is.',
  'home.install.routing.linux': 'built in, on PulseAudio or PipeWire',
  'home.install.routing.macos': 'driver ships with the app',
  'home.install.routing.windows': 'via VB-CABLE',
  'home.install.ext.title': 'Browser extension',
  'home.install.ext.body': 'Nothing installed on the system, no audio routing to set up. Purpose-built for the meeting sites it supports.',
  'home.install.ext.note': "Inside those pages your translated voice is offered to the site as a microphone, and the tab's own audio is what it translates back. Anywhere else — another app, a video outside the browser, system audio — is the desktop app's job.",
  'flow.youSpeak': 'you speak',
  'flow.translates': 'sokuji translates',
  'flow.virtualMic': 'virtual mic',
  'flow.anyApp': 'any app',
  'flow.theyHear': 'they hear',
  'flow.them': 'them',
  'flow.yourScreen': 'your screen',
  'flow.audience': 'your audience',
  'flow.onScreen': 'on screen',
  'providers.page.title': 'Where the translation actually happens',
  'providers.page.lead': 'Sokuji does not do the recognition or the translation itself — it captures your voice, sends it somewhere, and speaks the answer back into your call. You choose where that somewhere is. There are three routes, and you are never stuck on one.',
  'providers.routes.title': 'Three routes',
  'providers.local.chip': 'free · no account · offline',
  'providers.own.chip': 'you hold the key · you get the bill',
  'providers.managed.chip': 'metered · no subscription',
  'providers.table.title': 'Supported providers',
  'providers.table.lead': 'Sokuji ships adapters for these. “Speaks” means it can produce spoken translation rather than text only — which is what the flagship scenario needs. Where a Kizuna AI account is offered, the vendor actually running the model is named, because that is who receives your audio. “Face-to-face” means two people at one microphone, each hearing the other’s translation in one earbud; the provider that can do it also tells several speakers on the other side apart.',
  'providers.col.provider': 'Provider',
  'providers.col.runs': 'How it runs',
  'providers.col.speaks': 'Speaks',
  'providers.col.faceToFace': 'Face-to-face',
  'providers.col.setup': 'Setup',
  'providers.speaks.yes': 'yes',
  'providers.speaks.textOnly': 'text only',
  'providers.speaks.depends': 'depends',
  'providers.faceToFace.yes': 'yes',
  'providers.faceToFace.no': 'no',
  'providers.guide': 'Guide',
  'providers.run.local': 'On your machine, engine downloaded to match your GPU',
  'providers.run.direct': 'Your key, direct to {vendor}',
  'providers.run.keyOrAccount': 'Your key, or a Kizuna AI account — vendor: {vendor}',
  'providers.run.webrtc': 'Your key, WebRTC transport',
  'providers.cost.title': 'What the account costs',
  'providers.cost.lead': 'Only this route costs anything. Sokuji itself is free and open source, and on-device inference is free with it.',
  'providers.cost.metered.title': 'metered, never monthly',
  'providers.cost.metered.body': 'You top up a balance and it is drawn down as you use it. A month you do not use costs you nothing, and there is no seat count.',
  'providers.cost.metered.body2': 'There is no single price to quote here, because each vendor meters differently — some by audio duration, some by what the model processes. The rate that applies is the one shown against that vendor in your account, before you spend anything.',
  'providers.cost.path.title': 'still not in the path',
  'providers.cost.path.body': 'Paying us does not put us between you and the model. We hand your device a credential and count what it spends; the audio goes device to vendor exactly as it does with your own key.',
  'providers.cost.wired': 'See how that is wired',
  'providers.cost.note': 'Balance is valid for one year. That bound exists because unspent credit sits on our books as a liability, not to push anyone into spending it.',
  'providers.own.title': 'Your own API key',
  'providers.own.body': 'Sign up with any provider below, paste the key into Sokuji, and pay that provider directly. We wrote a walkthrough with screenshots for each one. Nothing routes through us at all.',
  'providers.managed.title': 'Through a Kizuna AI account',
  'providers.managed.body': 'For people who would rather not deal with developer consoles. We hold the key and settle the bill; Soniox or Qwen still runs the model. Your audio goes straight to them — we are not in the path.',
  'providers.local.title': 'On your own machine',
  'providers.local.body': 'Download an inference engine matched to your GPU and run recognition and translation locally. Free, works with the network off, and nothing leaves the device — not even to us.',
  'providers.price.title': 'Why no rate is printed here',
  'providers.price.body': 'Every vendor meters differently — per minute, per character, per token, per request — and the same minute of speech costs different amounts depending on what was said. A single number on this page would be wrong for almost everyone who read it. The rate that applies to you is shown in your account, against the vendor it belongs to.',
  'localai.page.title': 'On-device is not the fallback',
  'localai.page.lead': 'Sokuji ships free on-device recognition, translation and speech synthesis. It is not there for when the network drops — it is a first-class path, and we expect it to become the default one.',
  'localai.honest.lead': 'We would rather say this plainly than oversell it.',
  'localai.tomorrow.strong': 'Nobody should have to hand their conversation to a large AI company in order to get AI at all.',
  'localai.data.title': 'What each route means for your data',
  'localai.data.lead': 'This is the only one of the three where the answer needs no qualification.',
  'localai.col.ownKey': 'Your own key',
  'localai.col.account': 'Kizuna AI account',
  'localai.col.onDevice': 'On-device',
  'localai.row.leaves': 'Audio leaves your machine',
  'localai.row.hear': 'Someone else can hear it',
  'localai.row.offline': 'Works with the network off',
  'localai.row.costs': 'What it costs',
  'localai.val.toVendor': 'to the vendor',
  'localai.val.never': 'never',
  'localai.val.vendorCan': 'the vendor can',
  'localai.val.nobody': 'nobody',
  'localai.val.no': 'no',
  'localai.val.yes': 'yes',
  'localai.val.vendorBills': 'the vendor bills you',
  'localai.val.meteredByUs': 'metered by us',
  'localai.val.free': 'free',
  'localai.what.title': 'What runs on your machine',
  'localai.chain.title': 'Recognition, translation, speech',
  'localai.chain.body': 'The whole chain runs locally: ASR turns your voice into text, MT translates it, TTS speaks it. No part of it needs a network.',
  'localai.engine.title': 'An engine matched to your hardware',
  'localai.engine.body': 'Sokuji detects your GPU and downloads the engine package built for it, then tells you which one it picked and how large it is.',
  'localai.nokey.title': 'No key, no account, no meter',
  'localai.nokey.body': 'Nothing to register for and nothing to spend. Your own hardware is the entire cost.',
  'localai.honest.title': 'Why we ship it even though cloud models are still better',
  'localai.today.title': 'where it stands today',
  'localai.today.body': 'A large hosted model is still ahead on accuracy and on latency, and for a high-stakes call you will probably want one. On-device output is good enough for a great many conversations — and it is the only configuration that involves nobody but you.',
  'localai.tomorrow.title': 'where we think it goes',
  'localai.tomorrow.body': 'Models keep getting smaller, runtimes keep getting better, and the accelerators in ordinary laptops keep getting faster. We do not expect the gap to last, and we are not waiting for it to close before treating local as a first choice.',
  'privacyPage.hero.kicker': 'Privacy',
  'privacyPage.hero.title': 'Your meeting content travels only between you and the AI you chose.',
  'privacyPage.hero.lead': 'However you use Sokuji, voices, screen pictures, transcripts and translations go straight from your device to the AI provider you picked, never through Kizuna AI. The only difference is who settles the bill with that provider.',
  'privacyPage.hero.exception': 'The one exception: when you clone a voice with a Kizuna AI account, the voice sample you record passes through our server on its way to Soniox. We store no voice at all — the sample is discarded once it is handed over, and our server keeps only the voice ID Soniox returns.',
  'privacyPage.hero.flowLabel': 'Meeting content goes straight from your device to the AI provider you chose; with a Kizuna AI account, a separate line to Kizuna AI carries only your account, balance and usage.',
  'privacyPage.node.device': 'Your device',
  'privacyPage.node.provider': 'AI provider',
  'privacyPage.node.kizuna': 'Kizuna AI',
  'privacyPage.node.stays': 'Content stays on this machine',
  'privacyPage.way.ownKey.title': 'With your own key',
  'privacyPage.way.ownKey.body': 'You pay the AI provider directly, and your sessions never touch our servers.',
  'privacyPage.way.ownKey.note': 'Kizuna AI: not involved',
  'privacyPage.way.kizuna.title': 'With a Kizuna AI account',
  'privacyPage.way.kizuna.body': 'We issue a short-lived key and bill by usage; your content still goes straight to the AI provider.',
  'privacyPage.way.kizuna.note': 'The dashed line carries only your account, balance and usage',
  'privacyPage.way.local.title': 'On your own machine',
  'privacyPage.way.local.body': 'When you use on-device models, they run on your computer and meeting content never leaves it.',
  'privacyPage.way.local.note': 'Works offline once a model is downloaded; until then, Microsoft\'s online engines may be used automatically (see below)',
  'privacyPage.dest.title': 'Where each kind of data goes',
  'privacyPage.dest.lead': 'This is the complete list. Meeting content not listed here never leaves your device.',
  'privacyPage.dest.contacts': 'Sokuji also makes a few connections that carry no meeting content: downloading on-device models, checking for updates, and sending usage statistics and error reports. Note that the engines marked “Online” in local inference (Edge TTS for speech, Bing Translator) do not run on your computer: they are used automatically when no on-device model is ready, or when you choose them, and the text to be spoken or translated is then sent to Microsoft.',
  'privacyPage.dest.provider.title': 'The AI provider you chose',
  'privacyPage.dest.provider.who': 'Does the recognition, translation and speech',
  'privacyPage.dest.provider.voices': 'Your voice and the other side\'s',
  'privacyPage.dest.provider.screen': 'Screen pictures (when screen context is on)',
  'privacyPage.dest.provider.text': 'Transcripts and translations',
  'privacyPage.dest.provider.voiceSample': 'Voice samples for voice cloning',
  'privacyPage.dest.provider.foot': 'Handled under each provider\'s own privacy terms.',
  'privacyPage.dest.provider.link': 'See the provider list',
  'privacyPage.dest.kizuna.title': 'Kizuna AI',
  'privacyPage.dest.kizuna.who': 'Only when you use a Kizuna AI account',
  'privacyPage.dest.kizuna.email': 'Your email and the name you entered at sign-up (any nickname is fine)',
  'privacyPage.dest.kizuna.logins': 'Sign-in records (IP address, device, approximate location)',
  'privacyPage.dest.kizuna.billing': 'Balance, top-ups and per-session usage, plus payment details from Stripe used to verify payments',
  'privacyPage.dest.kizuna.feedback': 'Feedback you choose to send',
  'privacyPage.dest.kizuna.foot': 'No pictures, transcripts or translations, and no voices except voice-clone samples passed on to Soniox (see above).',
  'privacyPage.dest.device.title': 'Stays with you',
  'privacyPage.dest.device.who': 'In every mode',
  'privacyPage.dest.device.history': 'Session history and exported files',
  'privacyPage.dest.device.settings': 'Your settings and your own keys: the desktop app keeps them on your computer; the mobile app keeps its settings on your phone; the browser extension syncs them through your Google account when Chrome Sync is on',
  'privacyPage.dest.device.local': 'All meeting content when you use only on-device models',
  'privacyPage.dest.device.foot': 'Delete them in the app, clear the browser\'s data, remove the app\'s data folder, or uninstall the mobile app; exported files stay wherever you saved them.',
  'privacyPage.third.posthog.title': 'Usage statistics · PostHog',
  'privacyPage.third.posthog.body': 'We use PostHog (United States) for usage statistics and error reports — which provider you used, session length, errors, device and operating system, and in the browser extension the hostname of the page where you open it — to improve the product. Once you sign in, these are linked to your account ID, email and the name you entered. They never include meeting content. There is currently no switch in the app to turn them off.',
  'privacyPage.third.stripe.title': 'Payments · Stripe',
  'privacyPage.third.stripe.body': 'Top-ups are processed by Stripe, and your full card number goes only to Stripe — we never see it. To reconcile and verify payments, we receive some payment details back from Stripe, such as the payer\'s name, email and billing address, and the card brand and last four digits.',
  'privacyPage.stored.title': 'What we keep, and for how long',
  'privacyPage.stored.lead': 'The data below is stored on our servers only if you use a Kizuna AI account.',
  'privacyPage.stored.col.data': 'Data',
  'privacyPage.stored.col.why': 'Why we need it',
  'privacyPage.stored.col.keep': 'How long',
  'privacyPage.stored.email.data': 'Email and name',
  'privacyPage.stored.email.why': 'Signing in, verifying the account, granting trial credit',
  'privacyPage.stored.email.keep': 'While the account exists',
  'privacyPage.stored.logins.data': 'Sign-in records (IP address, device, approximate location)',
  'privacyPage.stored.logins.why': 'Keeping you signed in, and looking into account problems',
  'privacyPage.stored.logins.keep': 'Until the account is deleted',
  'privacyPage.stored.abuseIps.data': 'Anti-abuse IP records',
  'privacyPage.stored.abuseIps.why': 'Stopping the same person from claiming the trial credit twice',
  'privacyPage.stored.abuseIps.keep': '90 days after last seen',
  'privacyPage.stored.trial.data': 'Trial-credit records (a one-way hash of your email; if a claim was refused, the matching IP address)',
  'privacyPage.stored.trial.why': 'Stopping the same email address or person from claiming the trial credit again',
  'privacyPage.stored.trial.keep': 'Kept long-term, including after account deletion',
  'privacyPage.stored.billing.data': 'Balance, top-ups and usage, plus payment details from Stripe used to verify payments',
  'privacyPage.stored.billing.why': 'Billing, verifying payments, and showing them to you on your account page',
  'privacyPage.stored.billing.keep': 'While the account exists; after deletion, as required by applicable local law',
  'privacyPage.stored.feedback.data': 'Feedback',
  'privacyPage.stored.feedback.why': 'Replying to you',
  'privacyPage.stored.feedback.keep': 'Kept in our support mailbox',
  'privacyPage.stored.deletion': 'You can delete your account at any time from your account page. Deletion removes your email, name, sign-in records, linked sign-in methods and cloned voices; anti-abuse IP records are removed on the schedule above; trial-credit records are kept so the trial cannot be claimed again; billing records, including payment details from Stripe used to verify payments, are kept as required by law.',
  'privacyPage.check.title': 'Don\'t just take our word for it',
  'privacyPage.check.lead': 'Sokuji is open source (AGPL-3.0). Anyone technical can check what this page says about where meeting content goes.',
  'privacyPage.check.code.title': 'Read the code',
  'privacyPage.check.code.body': 'Every place meeting content is sent is written in src/providers/, src/lib/edge-tts/ and src/lib/bing-translator/, and none of them is Kizuna AI (except voice-clone samples, described above).',
  'privacyPage.check.network.title': 'Watch the traffic',
  'privacyPage.check.network.body': 'Run a meeting with your browser\'s developer tools or a proxy open: every request carrying meeting content goes straight to the AI provider.',
  'privacyPage.check.offline.title': 'Go offline',
  'privacyPage.check.offline.body': 'With only on-device models, once they are downloaded, Sokuji keeps working with the network unplugged.',
  'privacyPage.promise.title': 'Our commitments',
  'privacyPage.promise.noSale': 'We don\'t sell or rent your data, and we share it with no one beyond the services listed above and the infrastructure providers named in our Privacy Policy.',
  'privacyPage.promise.noTraining': 'We don\'t train models on your data or analyse your meetings — we never receive their content in the first place.',
  'privacyPage.promise.trust': 'Our account server is not open source, so this part you have to take on trust. If you\'d rather not, Sokuji works fully without signing in: bring your own key, or run on your own machine.',
  'privacyPage.end.note': 'This is the plain-language summary. The legally binding terms are in our formal Privacy Policy.',
  'privacyPage.end.cta': 'Privacy Policy',
  'download.page.title': 'Get Sokuji',
  'download.page.lead': 'Free and open source on Windows, macOS and Linux. Each one asks something slightly different of you before the first run, and this page says what.',
  'download.desktop.title': 'Desktop',
  'download.desktop.lead': 'The desktop app registers as a microphone on your system, so any application that lets you pick a mic can use it.',
  'download.linux.note': 'requires pulseaudio-utils',
  'download.windows.note': 'vb-cable installed separately',
  'download.macos.note': 'signed and notarized · read the install guide',
  'download.macos.noteLead': 'signed and notarized ·',
  'download.ext.addEdge': 'Add to Edge',
  'download.ext.addChrome': 'Add to Chrome',
  'download.macos.bodyBefore': 'The audio driver ships inside the installer, for Apple silicon and Intel. The app itself is',
  'download.macos.signed': 'signed and notarized',
  'download.macos.bodyAfter': ', so it installs like any other Mac app.',
  'download.need.linuxBefore': 'PulseAudio or PipeWire running, plus',
  'download.need.linuxMid': 'for',
  'download.ext.title': 'Browser extension',
  'download.ext.lead': 'Nothing installed on the system, no audio routing to arrange. Purpose-built for meeting sites, where it can inject a virtual microphone into the page itself.',
  'download.ext.browser': 'Chrome & Edge',
  'download.ext.browserBody': 'Chromium 116 or newer, side panel API. Installs in one click from the store.',
  'download.ext.worksOn': 'works on',
  'download.ext.elsewhere': 'for anything else, use the desktop app',
  'download.need.title': 'Before you install',
  'download.need.col': 'What you need',
  'download.need.linux': 'PulseAudio or PipeWire running, plus pulseaudio-utils for pactl',
  'download.need.windows': 'VB-CABLE for audio routing; the installer points you at it',
  'download.need.macos': 'The bundled SokujiVirtualAudio driver',
  'download.need.any': 'Any platform',
  'download.need.anyBody': 'A provider: on-device inference, your own API key, or a Kizuna AI account. None of them is required to install.',
  'download.need.nothing': 'Nothing',
  'download.need.nothingBody': 'No account is needed to download, install or use Sokuji with your own key or on-device.',
  'download.linux.title': 'Linux',
  'download.linux.body': 'Debian package or AppImage, x64 and arm64. The virtual microphone is created by Sokuji itself through PulseAudio or PipeWire — nothing else to install.',
  'download.windows.title': 'Windows',
  'download.windows.body': 'Code-signed, so it installs without the usual unknown-publisher warnings — thanks to SignPath Foundation, who sign free software at no charge. Audio routing goes through VB-CABLE.',
  'download.macos.title': 'macOS',
  'download.macos.body': 'The audio driver ships inside the installer. The app itself is signed and notarized, so it installs like any other Mac app.',
  'home.platform.linux.body': 'The virtual microphone is created by Sokuji itself through PulseAudio or PipeWire — nothing else to install.',
  'home.platform.cta': 'Download',
  'home.platform.windows.body': 'Code-signed, so it installs without the usual warnings — thanks to SignPath Foundation, who sign it for free. Routing uses VB-CABLE.',
  'home.platform.macos.body': 'The audio driver ships with the installer. The app is signed and notarized, so it installs like any other Mac app.',
  'home.faq.title': 'Why it works this way',
  'home.faq.lead': 'Most of the questions we get are really questions about the choices behind the product. Here they are, answered plainly.',
  'faq.audio.q': 'Do you get my audio or my meeting content?',
  'faq.audio.a': 'No. Audio, screen pictures, transcripts and translations go from your device straight to the AI provider you chose. This holds when the key comes from a Kizuna AI account too: we issue a short-lived key and meter usage, and the vendor does the rest. What we store is what an account needs — email, sign-in records, balance and usage — and never meeting content. The full list is on the privacy page.',
  'faq.other.q': 'Why doesn’t the other person need to install anything?',
  'faq.other.a': 'Because we think crossing a language is the job of whoever wants to be understood, not of everyone listening. Sokuji translates on your side and speaks through a virtual microphone, so what reaches them is ordinary call audio.',
  'faq.service.q': 'Do I have to use your AI service?',
  'faq.service.a': 'No. Bring your own key for any supported vendor, or run on-device for free. The Kizuna AI account exists for people who would rather not deal with developer consoles — not to lock anyone in.',
  'faq.cost.q': 'What does Sokuji cost?',
  'faq.cost.a': 'Sokuji is free and open source, and on-device inference is free with it. Only the optional Kizuna AI account costs anything, and it is metered by what you actually use — never a monthly subscription, and a quiet month costs nothing. Top-up balance is valid for a year: that bound is bookkeeping, not a nudge to spend. Bring your own key or run locally and you never pay us at all.',
  'faq.believe.q': 'Why should I believe that?',
  'faq.believe.a': 'Partly, you shouldn’t — so here is the line. The app is AGPL-3.0 licensed, and the app is the piece that decides where your audio goes: all of it is at github.com/kizuna-ai-lab/sokuji, you can build it yourself, and you can watch it on the wire. Our account service is not open source, so what it does with account and usage data is a promise, not a proof. We would rather mark that line than blur it.',
  'faq.youtube.q': 'Can I use it to translate YouTube in real time?',
  'faq.youtube.a': 'You can, and nothing stops you — Sokuji translates whatever audio reaches your machine, a video included. It is just not what we build for. A video watched by a hundred thousand people, translated live, is the same work done a hundred thousand times; translated once by the publisher, it is done once. Creators do use Sokuji to produce those translations.',
  'faq.local.q': 'Is local AI just a fallback for when I’m offline?',
  'faq.local.a': 'No — it is a first-class path and has always been free. Models keep getting smaller, runtimes keep improving, and consumer accelerators keep getting faster. Nobody should have to hand their conversation to a large AI company to get AI at all.',
  'faq.platforms.q': 'Is Sokuji signed on macOS and Windows?',
  'faq.platforms.a': 'Yes. The Windows installers are signed thanks to SignPath Foundation, who sign free software at no charge. macOS builds are signed with an Apple Developer ID and notarized by Apple, from version 0.46.5.',
  'home.during.title': 'What it does during a call',
  'home.during.lead': 'Anyone can pipe speech through a model. Staying intelligible over a whole meeting — keeping pace, knowing when you have finished a thought, not echoing yourself — is audio engineering.',
  'feat.voice.t': 'Speak in your own voice',
  'feat.voice.b': 'Record a short clip once and your translated speech carries your voice instead of a stock one — or pick one from the provider’s library. Which voices you can choose from depends on the provider you are on.',
  'feat.tempo.t': 'Adaptive speech speed',
  'feat.tempo.b': 'The same sentence takes longer in some languages than others, so at a fixed rate the translation falls further behind with each one. Sokuji speeds the delivery up just enough to stay level.',
  'feat.turn.t': 'Semantic turn detection',
  'feat.turn.b': 'It waits for a finished thought, not merely a pause — so you are not cut off mid-sentence.',
  'feat.terms.t': 'Your words, your terms',
  'feat.terms.b': 'System instructions keep names, product terms and in-house jargon from being translated into mush.',
  'feat.overlay.t': 'Subtitle overlay',
  'feat.overlay.b': 'A separate window you can drag and resize, or captions laid over the meeting page itself.',
  'feat.offline.t': 'Works with the network off',
  'feat.offline.b': 'Sokuji detects your GPU, downloads the inference engine built for it, and runs recognition, translation and speech on your own machine — no key, no account, no meter. On-device is a first-class path here, not a fallback for when the connection drops.',
  'home.during.also': 'Also: push-to-translate, noise suppression, echo cancellation, partial-transcript translation, audio buffer tuning, swapping audio devices mid-session, the virtual microphone itself, and exporting a conversation as .txt or .json.',
  'home.carry.title': 'What we carry for you — and what stays yours',
  'home.carry.lead': 'Sokuji is free and open source, and stays that way. Separately, a Kizuna AI account can hold the key and settle the bill with a few AI vendors, for people who would rather not deal with developer consoles. We do not run the models — and it is a service you may never touch. Every convenience has an exit beside it.',
  'carry.ours': 'we carry this',
  'carry.yours': 'you can always do it yourself',
  'carry.account.t': 'One account, no consoles',
  'carry.account.b': 'We issue the credential and settle the bill. Soniox or Qwen still runs the model.',
  'carry.bill.t': 'One bill, metered',
  'carry.bill.b': 'We handle the payment plumbing and charge you for what you actually used.',
  'carry.cloud.t': 'Cloud models when you need them',
  'carry.cloud.b': 'The fastest and most accurate translation available today, on demand.',
  'carry.packaged.t': 'A packaged app',
  'carry.packaged.b': 'Desktop builds and a browser extension, installed in a couple of clicks.',
  'carry.ownkey.t': 'Bring your own key',
  'carry.ownkey.b': 'Ten-plus providers supported, each with a written walkthrough and screenshots.',
  'carry.direct.t': 'Pay the provider direct',
  'carry.direct.b': 'Leave us out of it entirely. Sokuji works without a Kizuna AI account at all.',
  'carry.machine.t': 'Run it on your own machine',
  'carry.machine.b': 'On-device inference, free, offline-capable, and shipped as a first choice.',
  'carry.source.t': 'Build it from source',
  'carry.source.b': 'Clone the app, read it, change it, ship your own build. AGPL-3.0.',
  'stat.langs': 'translation languages',
  'stat.ui': 'interface languages',
  'stat.sites': 'meeting sites in the extension',
  'stat.providers': 'AI providers, plus your own endpoint',
  'home.platforms.title': 'Every platform, and what each one asks of you',
  'download.builds': 'builds',
  'download.readGuide': 'Read the install guide',
  'home.install.flowLabel': 'Your voice, translated, reaching the other side through a virtual microphone',
  'home.routing.title': 'Your meeting content does not route through us',
  'home.routing.lead': 'Even when the key comes from your Kizuna AI account, audio, screen pictures and translations go from your device straight to the vendor running the model — and if the model you chose is the on-device one, they do not go anywhere at all. We handle accounts and billing, and what we store is account and usage data, never meeting content.',
  'routing.you': 'you',
  'routing.sokuji': 'sokuji, on your device',
  'routing.provider': 'the ai provider you chose',
  'routing.providerNote': 'cloud, or on your own machine',
  'routing.up': 'your audio, up',
  'routing.down': 'the translation, back',
  'routing.onDevice': 'on-device: both stay here',
  'routing.mic': 'virtual mic',
  'routing.app': 'any app',
  'routing.them': 'them',
  'routing.kizuna': 'Kizuna AI',
  'routing.kizunaCarries': 'short-lived key · usage billing · no meeting content',
  'routing.label': 'Meeting content goes from your device to the provider you chose and back; Kizuna AI sits on a separate branch carrying only a key, your account and usage',
  'routing.or': 'or',
  'scenarios.page.title': 'What are you trying to do?',
  'scenarios.page.lead': 'These are presets, not modes you are locked into — every part of what they set can be changed afterwards.',
  'scenarios.axes.title': 'What each one sets',
  'scenarios.axes.lead': 'Every one of them is the same three decisions, answered differently: whose audio gets translated, whether the result is spoken out loud, and what ends up on your screen.',
  'axis.sets': 'what it sets',
  'scenarios.whoPicks': 'Who picks it:',
  'scenarios.anyApp.lead': '“Any app” is literal.',
  'scenarios.anyApp.body': 'On the way out Sokuji is an ordinary microphone, so anything that lets you choose one can carry your translated voice — a browser tab, a recorder, streaming software, a game. On the way in it works from whatever your machine is playing, system audio included, so a video, a stream or a recording behaves exactly like a call.',
  'scenarios.fit.title': 'Not every provider can do every scenario',
  'scenarios.fit.lead': 'It turns on two properties. The first is whether the provider can produce a voice, and whether it can be kept quiet: one that returns only text cannot speak into the call, and one that always answers with audio cannot be held to subtitles. Every provider Sokuji offers today can do either, but the check stays for the next one. The second is face-to-face: two people at one microphone need a provider that can tell them apart, and only Soniox can, with your own key or through KizunaAI. Sokuji checks both when you choose the provider, so a scenario it cannot serve is closed off before the call rather than discovered during one.',
  'scenarios.fit.available': 'available',
  'scenarios.fit.unavailable': 'not available with that provider',
  'scenarios.fit.note': 'Understand what others say is the one that works everywhere: it starts as text, and the other side is read aloud only if you switch on Translation I hear. The last column is Soniox, with your own key or through KizunaAI.',
  'provider.textOnly': 'returns text\nonly',
  'provider.alwaysSpeaks': 'always\nspeaks',
  'provider.either': 'can do\neither',
  'provider.faceToFace': 'either, and\nface-to-face',
  'spoken.yesVoice': 'yes — a synthetic voice into the call',
  'spoken.yesBoth': 'yes — your side into the call, theirs to you',
  'spoken.yesEarbuds': 'yes — each of you hears the other in one earbud',
  'spoken.optional': 'no — unless you switch on Translation I hear',
  'flow.you': 'you',
  'flow.subtitles': 'subtitles',
  'flow.anyAppSystem': 'any app\nor system audio',
  'flow.yourHeadphones': 'your headphones',
  'flow.yourEarbud': 'your earbud',
  'flow.theirEarbud': 'their earbud',
  'flow.sameScreen': 'the same screen',
  'scenario.understand-others.who': 'You are the listener, and the speaker is not going to meet you halfway.',
  'scenario.be-heard.who': 'Anyone speaking in a language the room does not share, in a call they did not necessarily set up.',
  'scenario.subtitle-myself.who': 'Presenters and streamers, where a second voice over your own would be worse than captions.',
  'scenario.two-way-voice.who': 'A real back-and-forth where neither side shares a language.',
  'scenario.two-way-text.who': 'Recorded meetings, note-taking, or any room where added audio would be unwelcome.',
  'scenario.face-to-face-voice.who': 'Two people in the same room without a shared language — at a desk, a counter, a kitchen table.',
  'scenario.face-to-face-text.who': 'The same room, where a voice in the ear is unwelcome, or the other person would rather read on their own phone.',
  'axis.whoseAudio': 'whose audio',
  'axis.spokenAloud': 'spoken aloud',
  'axis.onScreen': 'on screen',
  'audio.speaker': 'your microphone',
  'audio.participant': 'the other side',
  'audio.both': 'both sides',
  'audio.besideMe': 'both of you, at one microphone',
  'spoken.yes': 'yes',
  'spoken.no': 'no',
  'scenario.understand-others.onScreen': 'the translation of what they say',
  'scenario.understand-others.flow': 'them speaking → you read',
  'scenario.be-heard.onScreen': 'your words and the translation together',
  'scenario.be-heard.flow': 'you speaking → they hear',
  'scenario.subtitle-myself.onScreen': 'the translation, for your audience',
  'scenario.subtitle-myself.flow': 'you speaking → they read',
  'scenario.two-way-voice.onScreen': 'source and translation, both sides',
  'scenario.two-way-voice.flow': 'both directions · voice both ways',
  'scenario.two-way-text.onScreen': 'source and translation, both sides',
  'scenario.two-way-text.flow': 'both directions · text only',
  'scenario.face-to-face-voice.onScreen': 'both sides, each line tagged with the ear it plays in',
  'scenario.face-to-face-voice.flow': 'one microphone · an earbud each',
  'scenario.face-to-face-text.onScreen': 'source and translation, both sides',
  'scenario.face-to-face-text.flow': 'one microphone · text only',
  'scenario.understand-others.title': 'Understand what others say',
  'scenario.understand-others.body': 'Online meetings, classes, talks, videos, streams — read a live translation of what you hear.',
  'scenario.understand-others.detail': 'Online meetings, classes, talks, videos, streams — read a live translation of what you hear. Nothing is read aloud unless you switch on Translation I hear.',
  'scenario.be-heard.title': 'Be understood in a meeting',
  'scenario.be-heard.body': 'They hear your translated voice through a virtual microphone.',
  'scenario.be-heard.detail': 'They hear your translated voice through a virtual microphone. No install, no plugin, no captions to read on their side.',
  'scenario.subtitle-myself.title': 'Subtitle my own speech',
  'scenario.subtitle-myself.body': 'Talks, streams, presentations — your audience reads translated subtitles; no audio is generated.',
  'scenario.subtitle-myself.detail': 'Talks, streams, presentations — your audience reads translated subtitles; no audio is generated. You keep speaking in your own voice.',
  'scenario.two-way-voice.title': 'Two-way online conversation',
  'scenario.two-way-voice.body': 'They hear your translated voice; you read theirs as subtitles and can hear it too.',
  'scenario.two-way-voice.detail': "They hear your translated voice; you read theirs as subtitles and can hear it too. In the desktop app their translation is read aloud while Other's audio is one application, such as your meeting app; while it captures all system sound, you only read it.",
  'scenario.two-way-text.title': 'Two-way online conversation, subtitles only',
  'scenario.two-way-text.body': 'Bilingual captions, meeting minutes — both sides as text, no synthetic voice.',
  'scenario.two-way-text.detail': 'Bilingual captions, meeting minutes — both sides as text, no synthetic voice.',
  'scenario.face-to-face-voice.title': 'Face-to-face conversation',
  'scenario.face-to-face-voice.body': 'Two people at one computer, each speaking their own language; each wears one earbud and hears only the translation into their language.',
  'scenario.face-to-face-voice.detail': 'Two people at one computer, each speaking their own language; each wears one earbud and hears only the translation into their language. By default your earbud is the left channel and theirs the right.',
  'scenario.face-to-face-text.title': 'Face-to-face conversation, subtitles only',
  'scenario.face-to-face-text.body': 'Both read the same screen, or the other person opens the LAN caption page on their phone.',
  'scenario.face-to-face-text.detail': 'Both read the same screen, or the other person opens the LAN caption page on their phone. The LAN caption page is in the desktop app only.',
  'nav.install': 'Installation',
  'nav.platforms': 'Platforms',
  'nav.platformsOverview': 'Overview',
  'nav.aiProviders': 'AI Providers',
  'nav.kizunaAI': 'KizunaAI',
  'nav.kizunaAIFeatures': 'KizunaAI Features',
  'nav.providersOverview': 'Overview',
  'nav.privacy': 'Privacy',
  'nav.terms': 'Terms of Service',
  'footer.terms': 'Terms',
  'nav.commerce': 'Commerce Disclosure',
  'nav.feedback': 'Feedback',
  'nav.github': 'GitHub',

  // Tutorials
  'tutorials.zoom': 'Zoom',
  'tutorials.googleMeet': 'Google Meet',
  'tutorials.teams': 'Microsoft Teams',
  'tutorials.discord': 'Discord',
  'tutorials.slack': 'Slack',
  'tutorials.jitsi': 'Jitsi Meet',
  'tutorials.whereby': 'Whereby',
  'tutorials.gather': 'Gather',
  'tutorials.openai': 'OpenAI Setup',
  'tutorials.gemini': 'Gemini Setup',
  'tutorials.palabraai': 'PalabraAI Setup',
  'tutorials.volcengineAST2': 'Doubao AST 2.0 Setup',
  'tutorials.soniox': 'Soniox Setup',
  'tutorials.qwen': 'Qwen LiveTranslate Setup',
  'tutorials.kizunaQuickStart': 'Quick Start',
  'tutorials.twoWayMeeting': 'Two-Way Meeting Translation',
  'tutorials.understandOthers': 'Understand Meetings & Videos',
  'tutorials.beUnderstood': 'Speak in Meetings',
  'tutorials.subtitleMySpeech': 'Subtitles for Your Talks',
  'tutorials.twoWaySubtitles': 'Two-Way Subtitles',
  'tutorials.faceToFace': 'Face-to-Face Conversation',
  'tutorials.kizunaVoices': 'Voices and Voice Cloning',
  'tutorials.kizunaVocabulary': 'Custom Vocabulary',
  'tutorials.kizunaRegion': 'Processing Region',
  'tutorials.kizunaLatency': 'Sentence Breaks and Latency',
  'tutorials.kizunaBilling': 'Balance and Costs',
  'tutorials.localInference': 'Free (On-Device) Setup',
  'tutorials.localModels': 'Local Models and Licenses',
  'tutorials.realtimeTester': 'API Tester',

  // Landing Page
  'landing.title': 'Sokuji',
  'landing.slogan.line1': 'Speak your own language.',
  'landing.slogan.line2': 'Be heard in theirs.',
  'landing.tagline': 'AI-powered Live Speech Translation',
  'landing.subtitle': 'Your voice goes out in their language. What they say comes back in yours, as subtitles or read aloud to you. Sokuji is a virtual microphone between you and your meeting app, so the setup stays on your side — they just talk normally, with nothing to install and no link to join.',
  'landing.trust': 'Free & open source · on-device AI · no subscription',
  'landing.cta.extension': 'Get Browser Extension',
  'landing.cta.desktop': 'Download free',
  'landing.cta.docs': 'View Documentation',

  // Platform Selection
  'platform.title': 'Choose Your Platform',
  'platform.extension.title': 'Browser Extensions',
  'platform.extension.desc': 'For online meetings (Google Meet, Zoom, Teams, etc.)',
  'platform.extension.chrome': 'Chrome Web Store',
  'platform.extension.edge': 'Edge Add-ons',
  'platform.extension.brave': 'Brave (Coming Soon)',
  'platform.desktop.title': 'Desktop Applications',
  'platform.desktop.desc': 'For all scenarios - any website, app, or system audio',
  'platform.desktop.windows': 'Windows Installer (.exe)',
  'platform.desktop.macos.arm': 'macOS Installer - Apple Silicon (-arm64.pkg)',
  'platform.desktop.macos.intel': 'macOS Installer - Intel (-x64.pkg)',
  'platform.desktop.linux': 'Linux Package (.deb)',

  // Installation Guides
  'install.title': 'Installation Guides',
  'install.windows': 'Windows Installation Guide',
  'install.macos': 'macOS Installation Guide',
  'install.linux': 'Linux Installation Guide',

  // Docs Home
  'docs.title': 'Documentation',
  'docs.subtitle': 'Learn how to install and use Sokuji',
  'docs.gettingStarted': 'Getting Started',
  'docs.installation': 'Installation',
  'docs.configuration': 'Configuration',
  'docs.resources': 'Resources',
  'docs.card.participantAudio': 'Translate what the other side says: from a meeting app, a browser tab or all system audio.',
  'docs.card.subtitleMode': 'A slim floating subtitle bar to place over a meeting, a video or your slides.',

  // Supported Sites
  'sites.title': 'Supported Websites',
  'sites.subtitle': 'The Sokuji extension is compatible with the following video conferencing and communication platforms.',
  'sites.howToUse.title': 'How to Use',
  'sites.howToUse.desc': 'On any supported platform, simply select "Sokuji Virtual Microphone" as your microphone input in the platform\'s audio settings. Sokuji will then provide real-time translation of your speech.',
  'sites.needHelp.title': 'Need Help?',
  'sites.needHelp.desc': 'If you encounter any issues with a specific platform, please check our GitHub repository for troubleshooting guides and support.',
  'sites.visitPlatform': 'Visit Platform',
  'sites.tutorial': 'Tutorial',

  // Site Cards
  'sites.meet.name': 'Google Meet',
  'sites.meet.url': 'meet.google.com',
  'sites.meet.features': '',

  'sites.teams.name': 'Microsoft Teams',
  'sites.teams.url': 'teams.live.com / teams.microsoft.com',
  'sites.teams.features': 'Cross-platform compatibility|Personal and Enterprise editions',

  'sites.gather.name': 'Gather',
  'sites.gather.url': 'app.gather.town',
  'sites.gather.features': 'Spatial audio support',

  'sites.whereby.name': 'Whereby',
  'sites.whereby.url': 'whereby.com',
  'sites.whereby.features': 'Browser-based meetings',

  'sites.discord.name': 'Discord',
  'sites.discord.url': 'discord.com',
  'sites.discord.features': 'Voice channel support',

  'sites.slack.name': 'Slack',
  'sites.slack.url': 'app.slack.com',
  'sites.slack.features': 'Huddles and calls support',

  'sites.jitsi.name': 'Jitsi Meet',
  'sites.jitsi.url': 'meet.jit.si',
  'sites.jitsi.features': 'Open-source meeting support',

  'sites.zoom.name': 'Zoom',
  'sites.zoom.url': 'app.zoom.us',
  'sites.zoom.features': 'Web client support',

  // AI Providers
  'providers.title': 'Supported AI Providers',
  'providers.subtitle': 'Sokuji supports multiple AI providers for real-time speech translation. Each provider offers different capabilities, models, and pricing structures. Free mode requires no API key and runs on your device at no cost; only some voices need an internet connection.',
  'providers.setup.title': 'Setup Instructions',
  'providers.setup.desc': 'To use a cloud AI provider, obtain an API key from the provider\'s website and configure it in Sokuji\'s settings panel. Free mode requires no API key — just download the models and start translating.',
  'providers.choosing.title': 'Choosing a Provider',
  'providers.needHelp.title': 'Need Help?',
  'providers.needHelp.desc': 'For setup guides, troubleshooting, and provider comparisons, visit our GitHub repository.',
  'providers.docs': 'Documentation',
  'providers.setupTutorial': 'Setup Tutorial',

  // Provider Cards
  'providers.openai.name': 'OpenAI',
  'providers.openai.type': 'Real-time Audio API',
  'providers.openai.features': 'GPT-4o Realtime Preview models|8 premium voice options|Advanced turn detection modes|Built-in noise reduction|60+ languages supported|Template mode for custom prompts',
  'providers.openai.desc': 'Best for high-quality voice synthesis and advanced features',

  'providers.gemini.name': 'Google Gemini',
  'providers.gemini.type': 'Gemini Live API',
  'providers.gemini.features': 'Gemini 2.0 Flash Live models|30 unique voice personalities|Automatic turn detection|35+ languages with regional variants|Built-in transcription|High token limits (8192)',
  'providers.gemini.desc': 'Great for multilingual support and automatic processing',

  'providers.palabra.name': 'PalabraAI',
  'providers.palabra.type': 'WebRTC Translation Service',
  'providers.palabra.features': 'Real-time WebRTC translation|60+ source languages|40+ target languages|Low latency streaming|Automatic audio processing|Specialized for live translation',
  'providers.palabra.desc': 'Optimized for real-time translation with minimal latency',



  'providers.volcengineAST2.name': 'Doubao AST 2.0',
  'providers.volcengineAST2.type': 'Speech-to-Speech Translation',
  'providers.volcengineAST2.features': 'End-to-end speech-to-speech translation|Voice cloning, or a fixed voice from a library of 500+|Low-latency simultaneous interpreting|20 languages plus Cantonese and Wu, 8 of them spoken|App ID + Access Token, or an API key|ByteDance cloud infrastructure',
  'providers.volcengineAST2.desc': 'Doubao speech-to-speech simultaneous interpretation that clones the speaker’s voice or speaks in a fixed voice of your choice',
  'providers.soniox.name': 'Soniox',
  'providers.soniox.type': 'Speech-to-Speech Translation',
  'providers.soniox.features': 'Real-time speech-to-speech translation|Transcribe and translate 60+ languages in one API call|Automatic language detection|200 built-in voices and voice cloning|Spoken translation or subtitles only|Face-to-face conversation and speaker labels|Soniox cloud infrastructure',
  'providers.soniox.desc': 'Real-time speech-to-speech translation across 60+ languages with automatic language detection',

  'providers.qwen.name': 'Qwen LiveTranslate',
  'providers.qwen.type': 'Speech-to-Speech Interpretation',
  'providers.qwen.features': 'Real-time simultaneous interpretation in one streaming session (qwen3.8-livetranslate-flash-realtime)|60 languages, 29 of them spoken, with automatic source detection|47 preset voices, or your own voice cloned live with nothing to record|Preferred translations for names and terms|Spoken translation or subtitles only|Your own key from QwenCloud (international) or 千问云 (mainland China), or a Kizuna AI account|Alibaba Cloud infrastructure; desktop app and browser extension',
  'providers.qwen.desc': 'Simultaneous interpretation that can speak in your own voice, cloned live, across 60 languages',

  'providers.localInference.name': 'Free',
  'providers.localInference.type': 'On-Device AI (Edge)',
  'providers.localInference.features': 'Fully offline — no API key or internet needed|40+ ASR models covering 99+ languages|55+ translation language pairs + Qwen LLMs|136 TTS models across 53 languages|CPU (WASM) and WebGPU acceleration|Privacy-first — all data stays on-device',
  'providers.localInference.desc': 'Run everything on your device with no data leaving your machine — perfect for privacy-sensitive use cases',

  // Privacy Policy
  'privacy.title': 'Privacy Policy',
  'privacy.lastUpdated': 'Last Updated: November 3, 2026',
  'privacy.intro.title': 'Introduction',
  'privacy.intro.content': 'This Privacy Policy explains how Kizuna AI Lab ("we", "our" or "us") collects, uses, discloses, retains and protects information in connection with Sokuji, including the desktop application, the mobile application, the browser extension, the website at sokuji.kizuna.ai and Kizuna AI accounts (together, the "Services").',
  'privacy.guarantee.title': 'Meeting Content',
  'privacy.guarantee.content': 'Meeting content (audio, pictures of the window, tab or screen you choose for screen context, transcripts and translations) is transmitted directly from your device to the AI provider you select. We do not receive, process or store meeting content on our servers, subject only to the voice-sample exception described under "Transmission of Meeting Content". In particular, we do not store:',
  'privacy.guarantee.items': 'Audio recordings or other voice content|Pictures of your screen sent for screen context|Transcripts, translations or other conversation content',
  'privacy.collect.title': 'Information We Collect',
  'privacy.collect.content': 'The information described under Account Information, Sign-in Records, Anti-abuse Records and Billing, Usage and Payment Information is collected only if you create or use a Kizuna AI account. Usage analytics and error reports are collected from the desktop application, the mobile application, the browser extension and the website, whether or not you are signed in.',
  'privacy.account.title': 'Account Information',
  'privacy.account.content': 'If you create a Kizuna AI account, we collect:',
  'privacy.account.items': 'Email address: used for registration, sign-in, account verification, password recovery and granting trial credit.|Password: stored only as a salted scrypt hash; we never store or have access to your password in plain text.|Display name: the name you enter at sign-up, which may be any nickname.|Account creation date, and session tokens used to keep you signed in.',
  'privacy.signin.title': 'Sign-in Records',
  'privacy.signin.content': 'Each time you sign in, we record the IP address, the user agent (browser and device type) and the approximate location derived from the IP address (country, region, city and approximate coordinates). We use these records to keep you signed in, to secure your account and to investigate account problems.',
  'privacy.abuse.title': 'Anti-abuse Records',
  'privacy.abuse.content': 'To prevent the same person from claiming trial credit more than once, we record IP addresses associated with account activity. We also keep trial-credit records: a one-way hash of the email address that received the trial credit and, where a claim was refused, the matching IP address.',
  'privacy.billing.title': 'Billing, Usage and Payment Information',
  'privacy.billing.content': 'We record your balance, top-ups and per-session usage (such as duration, provider and region) in order to bill you and to show your usage to you. Payments are processed by Stripe; your full card number is provided only to Stripe and is never received by us. To reconcile and verify payments, we receive certain payment information from Stripe, such as the payer\'s name, email address and billing address, and the card brand and last four digits.',
  'privacy.collect.analytics.title': 'Usage Analytics and Error Reports',
  'privacy.collect.analytics.content': 'We use PostHog, located in the United States, to collect usage analytics and error reports, including:',
  'privacy.collect.analytics.items': 'Features used, the AI provider selected and session duration|Errors, error reports and performance measurements|Device, operating system and browser type|Language selections|In the browser extension, the website address (hostname) of the page on which you open the extension',
  'privacy.collect.analytics.identity': 'When you are signed in, this information is associated with your account ID, email address and display name. It does not include meeting content. The Services do not currently offer an in-app setting to disable analytics; you may request deletion of your analytics data as described under "Your Rights".',
  'privacy.feedback.title': 'Feedback and Correspondence',
  'privacy.feedback.content': 'If you send feedback or contact us, we receive the content of your message, your email address and technical information attached to it, such as your browser\'s user agent, the application version and device model and, if you are signed in, your account ID.',
  'privacy.localOnly.title': 'Information Stored on Your Device',
  'privacy.localOnly.content': 'The following information is stored on your device and is not transmitted to our servers:',
  'privacy.localOnly.items': 'Settings and your own API keys: the desktop application stores them locally on your computer. The mobile application keeps your settings on your phone. The browser extension stores them in the browser\'s extension storage; if Chrome Sync is enabled, your browser synchronizes them through your Google account under Google\'s privacy terms.|Session history and exported files.|All meeting content, when you use only on-device models.',
  'privacy.storage.transmission.title': 'Transmission of Meeting Content',
  'privacy.storage.transmission.content': 'Meeting content is transmitted from your device directly to the AI provider you select over encrypted connections. When you use a Kizuna AI account, we issue short-lived credentials for that provider and meter your usage, but the content itself does not pass through our servers. The mobile application uses Soniox through a Kizuna AI account and asks for your permission before it first sends speech; you can withdraw it under Settings > About. Engines marked "Online" in local inference (Edge TTS and Bing Translator) are not on-device models: they are used automatically when no on-device model is ready, or when you select them, and the text to be spoken or translated is then sent to Microsoft. Exception: when you create a cloned voice with a Kizuna AI account, the voice sample you record is transmitted through our servers to Soniox to create the voice. We do not store the voice sample; we retain only the voice identifier returned by Soniox, which is deleted when you delete the voice or your account.',
  'privacy.use.title': 'How We Use Information',
  'privacy.use.items': 'To provide, operate and maintain the Services and your account|To issue provider credentials, meter usage and bill you|To verify payments|To prevent abuse of trial credit|To secure accounts and investigate problems|To understand how the Services are used and to improve them|To respond to your feedback and requests|To comply with legal obligations',
  'privacy.thirdParty.title': 'Third-Party Services',
  'privacy.thirdParty.content': 'The following third parties receive information in connection with the Services. Each processes it under its own privacy terms.',
  'privacy.thirdParty.cloudflare.title': 'Cloudflare',
  'privacy.thirdParty.cloudflare.content': 'We use Cloudflare for website hosting, content delivery, our account and billing database (Cloudflare D1) and transactional email.',
  'privacy.thirdParty.zoho.title': 'Zoho',
  'privacy.thirdParty.zoho.content': 'Our email addresses, including support@kizuna.ai, privacy@kizuna.ai and contact@kizuna.ai, are hosted by Zoho. Feedback and messages you send us are stored in those mailboxes under Zoho\'s privacy terms.',
  'privacy.thirdParty.stripe.title': 'Stripe',
  'privacy.thirdParty.stripe.content': 'Stripe processes payments for top-ups. Stripe receives your payment details under Stripe\'s Privacy Policy and provides us with the payment information described under "Billing, Usage and Payment Information".',
  'privacy.thirdParty.posthog.title': 'PostHog',
  'privacy.thirdParty.posthog.content': 'PostHog, located in the United States, receives the usage analytics and error reports described under "Usage Analytics and Error Reports".',
  'privacy.thirdParty.soniox.title': 'Soniox',
  'privacy.thirdParty.soniox.content': 'When you use Soniox, with your own API key or through a Kizuna AI account, audio and, for speech output, the text to be spoken are sent to Soniox servers in the region you pick (United States, European Union or Japan) and processed under Soniox\'s Privacy Policy.',
  'privacy.thirdParty.alibaba.title': 'Alibaba Cloud (Qwen)',
  'privacy.thirdParty.alibaba.content': 'When you use Qwen LiveTranslate, audio and, when screen context is on, pictures of the one window, tab or screen you chose are sent to Alibaba Cloud\'s Qwen service. With your own API key, they go to the region the key was created in — Singapore or Beijing (mainland China), whichever the key belongs to — and are processed under the terms of the service that issued it (QwenCloud or 千问云). Through a Kizuna AI account, they go to the region you pick in Sokuji (Singapore or Beijing) and are processed under Alibaba Cloud\'s privacy policy.',
  'privacy.thirdParty.openai.title': 'OpenAI',
  'privacy.thirdParty.openai.content': 'When you use an OpenAI provider, audio and the related text are sent to OpenAI for processing under OpenAI\'s Privacy Policy.',
  'privacy.thirdParty.google.title': 'Google (Gemini)',
  'privacy.thirdParty.google.content': 'When you use Gemini, audio and the related text are sent to Google for processing under Google\'s privacy terms.',
  'privacy.thirdParty.palabra.title': 'Palabra',
  'privacy.thirdParty.palabra.content': 'When you use Palabra, audio and the related text are sent to Palabra for processing under Palabra\'s privacy policy.',
  'privacy.thirdParty.bytedance.title': 'ByteDance (Volcengine)',
  'privacy.thirdParty.bytedance.content': 'When you use Volcengine speech translation (Doubao), audio and the related text are sent to ByteDance\'s Volcengine service for processing under Volcengine\'s privacy terms.',
  'privacy.thirdParty.microsoft.title': 'Microsoft',
  'privacy.thirdParty.microsoft.content': 'Edge TTS and Bing Translator in local inference are used automatically when no on-device model is ready, or when you select them. When they are used, the text to be spoken or translated is sent to Microsoft for processing under Microsoft\'s privacy terms.',
  'privacy.thirdParty.huggingface.title': 'Hugging Face',
  'privacy.thirdParty.huggingface.content': 'When you download on-device models, the files are fetched from Hugging Face, which receives your IP address and request information under its own privacy policy.',
  'privacy.thirdParty.github.title': 'GitHub',
  'privacy.thirdParty.github.content': 'The desktop application checks GitHub for updates and downloads releases from GitHub, which receives your IP address and request information under GitHub\'s privacy terms.',
  'privacy.transfer.title': 'International Transfers',
  'privacy.transfer.content': 'The third parties above process information in several countries, including the United States, the European Union, Japan, Singapore and mainland China (if you choose the Beijing region for Qwen, or use Volcengine). By using the Services, you understand that your information may be transferred to and processed in these countries.',
  'privacy.storage.title': 'Data Storage and Security',
  'privacy.storage.content': 'Account, sign-in, billing and usage information is stored in Cloudflare D1, which encrypts stored data at rest using AES-256. Information is transmitted over encrypted (TLS) connections.',
  'privacy.retention.title': 'Data Retention',
  'privacy.retention.content': 'We retain information for the following periods:',
  'privacy.retention.items': 'Account information and sign-in records: until your account is deleted.|Anti-abuse IP records: 90 days after the IP address was last seen.|Trial-credit records (a one-way hash of the email address and, where a claim was refused, the matching IP address): retained after account deletion to prevent the trial credit from being claimed again.|Billing and usage records, including payment information received from Stripe: while your account exists and, after deletion, as required by applicable local law.|Feedback and correspondence: in our support mailbox.|Information stored on your device: until you delete it in the application, clear your browser\'s data, remove the application\'s data folder or uninstall the mobile application; exported files remain where you saved them.',
  'privacy.deletion.title': 'Account Deletion',
  'privacy.deletion.content': 'You may delete your account at any time from the account page of the Kizuna AI dashboard, or by emailing privacy@kizuna.ai from your account email address. When your account is deleted:',
  'privacy.deletion.items': 'Your email address, display name, sign-in records and linked sign-in methods are deleted.|Cloned voices are deleted, including the voice held by Soniox.|Anti-abuse IP records are deleted on the schedule described under "Data Retention".|Trial-credit records are retained as described under "Data Retention".|Billing and usage records, including payment information received from Stripe, are retained as required by applicable local law.|Analytics data is deleted on request.',
  'privacy.rights.title': 'Your Rights',
  'privacy.rights.content': 'Subject to applicable law, you may contact us at privacy@kizuna.ai to:',
  'privacy.rights.items': 'Access, correct or delete your personal information|Obtain a copy of your personal information|Request deletion of your analytics data|Object to or request restriction of certain processing|Ask how your information is used',
  'privacy.gdpr.title': 'Legal Bases for Processing (EEA and UK)',
  'privacy.gdpr.content': 'If you are in the European Economic Area or the United Kingdom, we process personal information to perform our contract with you (providing your account and billing), on the basis of our legitimate interests (securing the Services, preventing abuse and improving the Services) and to comply with legal obligations (such as retaining billing records). You may also lodge a complaint with your local data protection authority.',
  'privacy.children.title': 'Children\'s Privacy',
  'privacy.children.content': 'The Services are not intended for children under 13, and we do not knowingly collect personal information from children under 13.',
  'privacy.changes.title': 'Changes to This Privacy Policy',
  'privacy.changes.content': 'We may update this Privacy Policy from time to time. We will post the updated policy on this page and revise the "Last Updated" date above.',
  'privacy.contact.title': 'Contact Us',
  'privacy.contact.content': 'If you have questions about this Privacy Policy:',
  'privacy.contact.email': 'Email: contact@kizuna.ai',
  'privacy.contact.privacy': 'Privacy Requests: privacy@kizuna.ai',
  'privacy.contact.github': 'GitHub: github.com/kizuna-ai-lab/sokuji',

  // Terms of Service
  'tos.title': 'Terms of Service',
  'tos.lastUpdated': 'Last Updated: October 21, 2026',

  'tos.intro.title': 'Introduction and Acceptance',
  'tos.intro.content': 'Welcome to Sokuji ("Service"), a real-time AI-powered translation application provided by Kizuna AI Lab ("Company," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our browser extension, desktop application, and related services.',
  'tos.intro.agreement': 'By downloading, installing, accessing, or using Sokuji, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the Service.',

  'tos.definitions.title': 'Definitions',
  'tos.definitions.items': '"Service" refers to the Sokuji application, including the browser extension, desktop application, and all related features.|"User," "you," or "your" refers to any individual or entity that accesses or uses the Service.|"Balance" refers to the prepaid balance, denominated in United States dollars (USD), that is purchased from Kizuna AI Lab and used to access Kizuna AI-provided translation services.|"Third-Party API Key" refers to API credentials obtained directly from third-party AI service providers (such as OpenAI, Google, Palabra AI) by the User.|"Kizuna AI Service" refers to AI translation services provided through Kizuna AI Lab\'s infrastructure that are charged against your Balance.',

  'tos.service.title': 'Service Description',
  'tos.service.overview': 'Sokuji provides real-time AI-powered translation services through two usage modes:',
  'tos.service.freeUsage.title': 'Free Usage Mode (Third-Party API Keys)',
  'tos.service.freeUsage.items': 'Users can connect their own API keys from supported AI providers (OpenAI, Google Gemini, Soniox, Qwen LiveTranslate, Palabra AI, etc.)|No charges from Kizuna AI Lab; users are billed directly by their chosen AI provider|Users are responsible for managing their API key security and usage limits',
  'tos.service.tokenUsage.title': 'Prepaid Balance Usage Mode (Kizuna AI Service)',
  'tos.service.tokenUsage.items': 'Users purchase a prepaid Balance, denominated in United States dollars (USD), from Kizuna AI Lab|Your Balance is charged when using Kizuna AI-provided translation services|Translation is charged by actual usage as measured by the AI provider, and the amount depends on the provider you select|Estimated hourly rates are shown on your wallet page; because charging follows actual usage, the amount is normally lower',

  'tos.account.title': 'Account Registration',
  'tos.account.age': 'You must be at least 13 years old to use the Service. If you are under 18, you represent that you have your parent or guardian\'s permission to use the Service. By using the Service, you represent and warrant that you meet this age requirement.',
  'tos.account.content': 'To access certain features, including Balance-based services, you must create an account. You agree to:',
  'tos.account.items': 'Provide accurate, current, and complete information during registration|Maintain the security of your account credentials|Promptly update any information to keep it accurate and current|Accept responsibility for all activities that occur under your account',
  'tos.account.security': 'You are responsible for maintaining the confidentiality of your account password and for restricting access to your account. You must notify us immediately of any unauthorized use of your account.',
  'tos.account.inactivity': 'Accounts that remain inactive for 24 consecutive months (no login or service usage) may be subject to closure. We will send a notification at least 30 days before closing an inactive account. Any remaining Balance in inactive accounts will be forfeited upon account closure.',

  'tos.notifications.title': 'Notifications and Communications',
  'tos.notifications.methods': 'We may send you notices through the following methods: (a) in-app notifications within Sokuji; (b) email to the address associated with your account; (c) posting notices on our website. You are responsible for keeping your email address current.',
  'tos.notifications.consent': 'By creating an account, you consent to receive important service-related communications from us via these methods.',

  'tos.tokens.title': 'Balance and Payment',
  'tos.tokens.purchase.title': 'Balance Purchase',
  'tos.tokens.purchase.items': 'Balance can be purchased through the application using supported payment methods|All prices are displayed in the applicable currency at the time of purchase|Balance is non-transferable between accounts|Balance added by a top-up is valid for one (1) year from the date of the top-up that created it',
  'tos.tokens.tax': 'All prices displayed are inclusive of applicable consumption tax (Japan) or value-added tax. For purchases made outside Japan, local taxes may apply according to your jurisdiction.',
  'tos.tokens.expiration': 'We will send a reminder notification at least 30 days before your Balance expires. Expired Balance will be automatically forfeited and cannot be recovered, refunded, or extended. It is your responsibility to use your Balance before it expires.',
  'tos.tokens.promotional': 'We may add promotional Balance to your account free of charge, for example a trial credit when you verify your email address. Promotional Balance is valid for the period stated when it is granted — seven (7) days for the email-verification trial credit — and is used before purchased Balance. The 30-day reminder above does not apply to promotional Balance: the grant notice is the only reminder. Promotional Balance is not refundable or transferable and is forfeited on expiry.',
  'tos.tokens.consumption.title': 'Balance Consumption',
  'tos.tokens.consumption.content': 'Translation is charged by actual usage as measured by the AI service used, so the amount depends on the provider and model. Estimated hourly rates are shown on your wallet page, and because charging follows actual usage the amount is normally lower than the estimate. Your Balance is updated once the provider reports the usage, shortly after a session ends, rather than continuously during the session.',
  'tos.tokens.refund.title': 'Refund Policy',
  'tos.tokens.refund.items': 'Unused Balance: Refund requests for unused Balance may be submitted within 14 days of purchase. A processing fee may apply.|Consumed Balance: Balance that has been consumed is non-refundable.|Service Issues: If Balance was consumed due to technical issues as solely determined by Kizuna AI Lab, we may credit the affected amount back to your account, subject to the following conditions: (a) the issue must be reported within 30 days of occurrence; (b) the total credits per account shall not exceed 20% of the Balance purchased in that calendar year; (c) Kizuna AI Lab reserves the sole right to determine what constitutes a technical issue and whether a credit is warranted.|Refund Process: Contact support@kizuna.ai with your purchase details and reason for refund.',

  'tos.thirdParty.title': 'Free Services (Third-Party API Keys)',
  'tos.thirdParty.content': 'When using your own API keys from third-party providers:',
  'tos.thirdParty.items': 'You are solely responsible for obtaining and managing your API keys|You must comply with the terms of service of each third-party provider|You are responsible for all charges incurred from third-party providers|API keys are stored locally on your device and are never transmitted to our servers',
  'tos.thirdParty.noWarranty': 'We make no warranties regarding the availability, quality, or performance of third-party AI services. Any issues with third-party services should be directed to the respective provider.',

  'tos.use.title': 'Acceptable Use',
  'tos.use.permitted.title': 'Permitted Use',
  'tos.use.permitted.items': 'Personal real-time translation|Business communication translation|Educational purposes|Accessibility support',
  'tos.use.prohibited.title': 'Prohibited Activities',
  'tos.use.prohibited.items': 'Use the Service for any illegal or unauthorized purpose|Attempt to reverse engineer, decompile, or disassemble the Service|Interfere with or disrupt the Service or servers|Use the Service to transmit harmful, offensive, or illegal content|Resell, sublicense, or commercialize access to the Service without authorization|Circumvent any security measures or access restrictions|Use automated systems or bots to access the Service beyond normal usage|Violate any applicable laws or regulations',

  'tos.ip.title': 'Intellectual Property',
  'tos.ip.ownership': 'The Service, including all software, content, features, and functionality, is owned by Kizuna AI Lab and is protected by international copyright, trademark, and other intellectual property laws.',
  'tos.ip.license': 'Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use the Service for personal or internal business purposes.',
  'tos.ip.userContent': 'You retain ownership of any content you provide through the Service. Audio and translation content are processed in real-time and are not stored by us.',

  'tos.disclaimer.title': 'Disclaimers',
  'tos.disclaimer.asIs': 'Service Provided "AS IS"',
  'tos.disclaimer.items': 'Implied warranties of merchantability or fitness for a particular purpose|Accuracy, reliability, or completeness of translations|Uninterrupted or error-free operation|Freedom from viruses or other harmful components',
  'tos.disclaimer.accuracy': 'AI-powered translations may contain errors. Users should not rely solely on translations for critical communications, legal documents, medical information, or safety-critical applications.',
  'tos.disclaimer.availability': 'We do not guarantee that the Service will be available at all times. The Service may be subject to limitations, delays, and other problems inherent in the use of internet and electronic communications.',

  'tos.liability.title': 'Limitation of Liability',
  'tos.liability.items': 'Kizuna AI Lab shall not be liable for any indirect, incidental, special, consequential, or punitive damages|Our total liability shall not exceed the amount you paid for Balance in the twelve (12) months preceding the claim|We are not liable for any loss resulting from unauthorized access to your account|We are not liable for any issues arising from third-party AI services',

  'tos.indemnification.title': 'Indemnification',
  'tos.indemnification.content': 'You agree to indemnify and hold harmless Kizuna AI Lab, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorneys\' fees) arising from your use of the Service, your violation of these Terms, your violation of any rights of third parties, or your use of third-party API keys.',

  'tos.termination.title': 'Termination',
  'tos.termination.byUser.title': 'Termination by You',
  'tos.termination.byUser.items': 'You may stop using the Service and delete your account at any time|Any unused Balance may be forfeited (subject to refund policy)|Your right to access the Service will immediately cease',
  'tos.termination.byUs.title': 'Termination by Us',
  'tos.termination.byUs.items': 'We may suspend or terminate your access if you violate these Terms|We may terminate if your use poses a security risk|We may terminate if required to do so by law|We may terminate if we discontinue the Service',
  'tos.termination.discontinuation': 'If we discontinue the Service entirely, we will provide at least 30 days advance notice. In such case, users with unused Balance may request a pro-rata refund for Balance purchased within the 12 months preceding the discontinuation, subject to a processing fee. Refund requests must be submitted within 60 days of the discontinuation notice.',

  'tos.changes.title': 'Changes to Terms',
  'tos.changes.content': 'We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page, updating the "Last Updated" date, and sending an in-app notification or email for significant changes. Continued use of the Service after changes constitutes acceptance of the modified Terms.',

  'tos.governing.title': 'Governing Law and Dispute Resolution',
  'tos.governing.informalResolution': 'Before initiating formal legal proceedings, both parties agree to first attempt to resolve any dispute through good-faith negotiation. The complaining party shall send written notice describing the dispute to the other party. Both parties shall then have 60 days to attempt informal resolution. If the dispute cannot be resolved informally within this period, either party may proceed with formal legal action as described below.',
  'tos.governing.law': 'These Terms shall be governed by and construed in accordance with the laws of Japan, without regard to its conflict of law provisions.',
  'tos.governing.jurisdiction': 'Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Tokyo, Japan.',
  'tos.governing.language': 'In the event of any discrepancy between the English and Japanese versions of these Terms, the Japanese version shall prevail for users in Japan.',

  'tos.general.title': 'General Provisions',
  'tos.general.entireAgreement': 'These Terms, together with our Privacy Policy, constitute the entire agreement between you and Kizuna AI Lab regarding the Service.',
  'tos.general.severability': 'If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.',
  'tos.general.waiver': 'Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.',
  'tos.general.forceMajeure': 'Kizuna AI Lab shall not be liable for any failure or delay in performing its obligations under these Terms due to circumstances beyond its reasonable control, including but not limited to natural disasters, war, terrorism, riots, pandemics, government actions, power failures, internet or telecommunications failures, or failures of third-party service providers.',

  'tos.contact.title': 'Contact Information',
  'tos.contact.content': 'If you have any questions about these Terms, please contact us:',
  'tos.contact.legal': 'Legal: legal@kizuna.ai',
  'tos.contact.support': 'Support: support@kizuna.ai',
  'tos.contact.github': 'GitHub: github.com/kizuna-ai-lab/sokuji',

  // Dashboard Navigation
  'dashboard.nav.dashboard': 'Dashboard',
  'dashboard.nav.profile': 'Profile',
  'dashboard.nav.security': 'Security',
  'dashboard.nav.feedback': 'Feedback',
  'dashboard.nav.signOut': 'Sign out',
  'dashboard.user.fallback': 'User',
  'dashboard.sidebar.collapse': 'Collapse sidebar',
  'dashboard.sidebar.expand': 'Expand sidebar',

  // Dashboard Home
  'dashboard.home.welcomeBack': 'Welcome back, {name}',
  'dashboard.home.subtitle': 'Manage your account settings and preferences',
  'dashboard.home.accountOverview': 'Account Overview',
  'dashboard.home.accountStatus': 'Account Status',
  'dashboard.home.accountDetails': 'Account Details',
  'dashboard.home.quickActions': 'Quick Actions',
  'dashboard.home.name': 'Name',
  'dashboard.home.email': 'Email',
  'dashboard.home.emailVerified': 'Email Verified',
  'dashboard.home.accountType': 'Account Type',
  'dashboard.home.userId': 'User ID',
  'dashboard.home.created': 'Created',
  'dashboard.home.lastUpdated': 'Last Updated',
  'dashboard.home.notSet': 'Not set',
  'dashboard.home.verified': 'Verified',
  'dashboard.home.notVerified': 'Not Verified',
  'dashboard.home.anonymous': 'Anonymous',
  'dashboard.home.registered': 'Registered',
  'dashboard.home.editProfile': 'Edit Profile',
  'dashboard.home.securitySettings': 'Security Settings',
  'dashboard.home.na': 'N/A',

  // Dashboard Profile
  'dashboard.profile.title': 'Profile Settings',
  'dashboard.profile.subtitle': 'Manage your personal information',
  'dashboard.profile.personalInfo': 'Personal Information',
  'dashboard.profile.personalInfoDesc': 'Update your name and basic information',
  'dashboard.profile.nameLabel': 'Name',
  'dashboard.profile.namePlaceholder': 'Your name',
  'dashboard.profile.saveChanges': 'Save Changes',
  'dashboard.profile.profileUpdated': 'Profile updated successfully',
  'dashboard.profile.emailAddress': 'Email Address',
  'dashboard.profile.emailAddressDesc': 'Manage your email address and verification status',
  'dashboard.profile.currentEmail': 'Current Email',
  'dashboard.profile.verified': 'Verified',
  'dashboard.profile.notVerified': 'Not Verified',
  'dashboard.profile.resendVerification': 'Resend Verification',
  'dashboard.profile.verificationSent': 'Verification email sent! Check your inbox.',
  'dashboard.profile.changeEmailTitle': 'Change Email Address',
  'dashboard.profile.newEmailLabel': 'New Email Address',
  'dashboard.profile.newEmailPlaceholder': 'new@email.com',
  'dashboard.profile.newEmailHint': 'A verification email will be sent to the new address',
  'dashboard.profile.changeEmail': 'Change Email',
  'dashboard.profile.emailChangeSent': 'Verification email sent to your new address. Please check your inbox.',

  // Dashboard Security
  'dashboard.security.title': 'Security Settings',
  'dashboard.security.subtitle': 'Manage your password and account security',
  'dashboard.security.changePassword': 'Change Password',
  'dashboard.security.changePasswordDesc': 'Update your password to keep your account secure',
  'dashboard.security.currentPassword': 'Current Password',
  'dashboard.security.currentPasswordPlaceholder': 'Enter current password',
  'dashboard.security.newPassword': 'New Password',
  'dashboard.security.newPasswordPlaceholder': 'Minimum 8 characters',
  'dashboard.security.confirmPassword': 'Confirm New Password',
  'dashboard.security.confirmPasswordPlaceholder': 'Confirm new password',
  'dashboard.security.changePasswordBtn': 'Change Password',
  'dashboard.security.activeSessions': 'Active Sessions',
  'dashboard.security.activeSessionsDesc': 'Manage your active login sessions ({count} active)',
  'dashboard.security.current': 'Current',
  'dashboard.security.lastActive': 'Last active: {time}',
  'dashboard.security.loadingSessions': 'Loading sessions...',
  'dashboard.security.signOutOther': 'Sign Out All Other Sessions',
  'dashboard.security.deleteAccount': 'Delete Account',
  'dashboard.security.deleteAccountDesc': 'Permanently delete your account and all associated data',
  'dashboard.security.deleteWarning': 'This action is irreversible. All your data will be permanently deleted.',
  'dashboard.security.deleteBtn': 'Delete My Account',
  'dashboard.security.deleteConfirmText': 'To confirm deletion, please type DELETE below:',
  'dashboard.security.deleteConfirmPlaceholder': 'Type DELETE to confirm',
  'dashboard.security.cancel': 'Cancel',
  'dashboard.security.anonymousNote': 'Anonymous accounts cannot change passwords. Please convert to a full account first.',
  'dashboard.security.passwordMinLength': 'New password must be at least 8 characters',
  'dashboard.security.passwordMismatch': 'Passwords do not match',
  'dashboard.security.passwordIncorrect': 'Current password is incorrect',
  'dashboard.security.passwordSuccess': 'Password changed successfully. Other sessions have been logged out.',
  'dashboard.security.typeDelete': 'Please type DELETE to confirm',

  // Dashboard Feedback
  'dashboard.feedback.title': 'Send Feedback',
  'dashboard.feedback.subtitle': 'Report bugs, suggest features, or share your thoughts. We read every message and appreciate your input!',
  'dashboard.feedback.yourFeedback': 'Your Feedback',
  'dashboard.feedback.sendingAs': 'Sending as {email}',
  'dashboard.feedback.remainingCount': 'You have {remaining} of {limit} feedback messages remaining today',
  'dashboard.feedback.limitReached': 'Daily limit reached. You can send more feedback tomorrow.',
  'dashboard.feedback.feedbackType': 'Feedback Type',
  'dashboard.feedback.bugReport': 'Bug Report',
  'dashboard.feedback.bugReportDesc': 'Report a problem or issue',
  'dashboard.feedback.suggestion': 'Feature Suggestion',
  'dashboard.feedback.suggestionDesc': 'Suggest a new feature or improvement',
  'dashboard.feedback.other': 'Other',
  'dashboard.feedback.otherDesc': 'General feedback or questions',
  'dashboard.feedback.message': 'Message',
  'dashboard.feedback.messagePlaceholder': 'Describe your feedback in detail...',
  'dashboard.feedback.messageHint': '{count}/5000 characters (minimum 10)',
  'dashboard.feedback.sendFeedback': 'Send Feedback',
  'dashboard.feedback.verificationRequired': 'Email Verification Required',
  'dashboard.feedback.verificationRequiredDesc': 'Please verify your email address before submitting feedback. We need a verified email to respond to your feedback.',
  'dashboard.feedback.goToProfile': 'Go to Profile Settings',
  'dashboard.feedback.thankYou': 'Thank you! Your feedback has been sent successfully.',

  // Dashboard Wallet
  'dashboard.wallet.title': 'Wallet',
  'dashboard.wallet.subtitle': 'Manage your balance and top-up',
  'dashboard.wallet.balance': 'Balance',
  'dashboard.wallet.hoursLeft': 'Estimated time ({scenario})',
  'dashboard.wallet.hoursLeftAverage': 'Estimated time (your 30-day average, {rate}/hr)',
  'dashboard.wallet.hoursValue': '≈ {hours} h',
  'dashboard.wallet.recentRecords': 'Recent',
  'dashboard.wallet.allRecords': 'All records',
  'dashboard.wallet.fullPricing': 'Full price list',
  'dashboard.wallet.plan': 'Current Plan',
  'dashboard.wallet.last30Days': 'Last 30 Days',
  'dashboard.wallet.expiringSoon': '${amount} expires on {date}',
  'dashboard.wallet.expiringSoonPartial': '${amount} expires on {date}, ${total} within a month',
  'dashboard.wallet.accountFrozen': 'Your account is frozen. Please contact support.',
  'dashboard.wallet.topUp': 'Top Up',
  'dashboard.wallet.customAmount': 'Custom Amount',
  'dashboard.wallet.amountRange': 'Min: {min} - Max: {max}',
  'dashboard.wallet.youWillReceive': 'You will receive',
  'dashboard.wallet.paymentMethod': 'Payment method',
  'dashboard.wallet.methodCard': 'Card',
  'dashboard.wallet.methodAlipay': 'Alipay',
  'dashboard.wallet.continueWithAmount': 'Continue to checkout · {amount}',
  'dashboard.wallet.poweredByStripe': 'Payments are securely processed by Stripe.',
  'dashboard.wallet.alipayCharge': 'Charged in yen: {amount}',
  'dashboard.wallet.alipayRate': 'Rate: 1 USD = {rate} JPY (ECB, {date})',
  'dashboard.wallet.alipayRateChanged': 'The exchange rate has just been updated. Please check the new amount and try again.',
  'dashboard.wallet.otherPaymentMethods': 'Other payment methods',
  'dashboard.wallet.processing': 'Processing...',
  'dashboard.wallet.pricing.provider': "Soniox's billing, real-time streaming only",

  // THE CALCULATOR. Its scenario presets, and their titles and descriptions,
  // follow kizuna-ai-lab/sokuji's own first-run wizard
  // (`setup.scenarios.*` there) so the dashboard and the app name the same
  // thing the same way, as are /scenarios' `scenario.<id>.title`/`.body`. The
  // two repos cannot share catalogs: `app-scenarios.fixture.ts` pins the
  // wizard's strings and `scenario-copy.test.ts` holds both pages to them, so
  // when the app rewords a scenario, update the fixture and these together.
  'dashboard.wallet.pricing.calcTitle': 'Cost calculator',
  'dashboard.wallet.pricing.calcScenario': 'What are you using it for?',
  'dashboard.wallet.pricing.calcEstimate': 'Estimated cost',
  'dashboard.wallet.pricing.calcFactors': 'What makes up that estimate',
  'dashboard.wallet.pricing.scenario.understand-others.title': 'Understand what others say',
  'dashboard.wallet.pricing.scenario.understand-others.desc': 'Online meetings, classes, talks, videos, streams — read a live translation of what you hear.',
  'dashboard.wallet.pricing.scenario.be-heard.title': 'Be understood in a meeting',
  'dashboard.wallet.pricing.scenario.be-heard.desc': 'They hear your translated voice through a virtual microphone.',
  'dashboard.wallet.pricing.scenario.subtitle-myself.title': 'Subtitle my own speech',
  'dashboard.wallet.pricing.scenario.subtitle-myself.desc': 'Talks, streams, presentations — your audience reads translated subtitles; no audio is generated.',
  'dashboard.wallet.pricing.scenario.two-way-voice.title': 'Two-way online conversation',
  'dashboard.wallet.pricing.scenario.two-way-voice.desc': 'They hear your translated voice; you read theirs as subtitles and can hear it too.',
  'dashboard.wallet.pricing.scenario.two-way-text.title': 'Two-way online conversation, subtitles only',
  'dashboard.wallet.pricing.scenario.two-way-text.desc': 'Bilingual captions, meeting minutes — both sides as text, no synthetic voice.',
  'dashboard.wallet.pricing.scenario.face-to-face-voice.title': 'Face-to-face conversation',
  'dashboard.wallet.pricing.scenario.face-to-face-voice.desc': 'Two people at one computer, each speaking their own language; each wears one earbud and hears only the translation into their language.',
  'dashboard.wallet.pricing.scenario.face-to-face-text.title': 'Face-to-face conversation, subtitles only',
  'dashboard.wallet.pricing.scenario.face-to-face-text.desc': 'Both read the same screen, or the other person opens the LAN caption page on their phone.',
  'dashboard.wallet.pricing.engine': 'Engine',
  'dashboard.wallet.pricing.region': 'Region',
  'dashboard.wallet.pricing.regionSg': 'Singapore',
  'dashboard.wallet.pricing.regionCn': 'Beijing',
  'dashboard.wallet.pricing.scenario.custom.title': 'Custom',
  'dashboard.wallet.pricing.scenario.custom.desc': 'Set each option below to match how you use it. Changing any option selects this.',
  'dashboard.wallet.pricing.sideThem': 'What they say',
  'dashboard.wallet.pricing.sideMe': 'What I say',
  'dashboard.wallet.pricing.output.off': 'Not translated',
  'dashboard.wallet.pricing.output.text': 'Subtitles',
  'dashboard.wallet.pricing.output.voiceThem': 'Read aloud to me',
  'dashboard.wallet.pricing.output.voiceMe': 'Read aloud to them',
  'dashboard.wallet.pricing.shareThem': 'How much they talk',
  'dashboard.wallet.pricing.shareMe': 'How much I talk',
  'dashboard.wallet.pricing.shareValue': '{count}% · about {minutes} min an hour',
  'dashboard.wallet.pricing.screenContext': 'Screen context',
  'dashboard.wallet.pricing.screenContextHint': 'The Qwen engine sees one window, tab or screen you choose; billed per hour of speech, per direction.',
  'dashboard.wallet.pricing.balanceLasts': 'Your balance of {balance} covers about {hours} h',
  'dashboard.wallet.pricing.balanceLastsUnderOne': 'Your balance of {balance} covers less than an hour',
  'dashboard.wallet.pricing.trait.soniox': '60 languages translated any to any, as subtitles or speech; preset voices, or record a clip to make your own; face to face on one computer.',
  'dashboard.wallet.pricing.trait.qwen': "Can read translations aloud in each speaker's own voice, cloned live sentence by sentence with nothing to record first; speaks 29 languages and subtitles 60.",
  'dashboard.wallet.pricing.sharedActive': 'Estimated on one shared stream: face to face, both directions share one connection.',
  'dashboard.wallet.pricing.sharedHint': 'Face to face, or both sides on one shared stream: one connection fewer, about {amount} less an hour.',
  'dashboard.wallet.pricing.needOneSide': 'Translate at least one side to see a price.',
  'dashboard.wallet.pricing.partConnection': 'Connection × {count}',
  'dashboard.wallet.pricing.partText': 'Text',
  'dashboard.wallet.pricing.partVoice': 'Voice',
  'dashboard.wallet.pricing.partScreen': 'Screen context',
  'dashboard.wallet.pricing.partTotal': 'Total',
  'dashboard.wallet.pricing.colItem': 'Item',
  'dashboard.wallet.pricing.colUnitPrice': 'Unit price',
  'dashboard.wallet.pricing.colHourly': 'Per hour',
  'dashboard.wallet.pricing.usePerConnectedHour': '{count} tokens per connected hour',
  'dashboard.wallet.pricing.usePerSpeechHour': '{count} tokens per hour of speech',
  'dashboard.wallet.pricing.usePerSpokenHour': '{count} tokens per hour read aloud',
  'dashboard.wallet.pricing.usePerScreenHour': '{count} tokens per hour of speech with screen context',
  'dashboard.wallet.pricing.providerQwen': '{name} billing, real-time streaming only',

  'dashboard.wallet.pricing.perHourApprox': '≈ {amount}/hr',
  'dashboard.wallet.pricing.assumptions': "Each translated direction is one connection, charged for every connected minute whether or not anyone speaks. Text is charged for the time the translated person speaks, and voice only for what is read aloud. Each person's speaking time is set separately, so turning a side on only ever adds that side's cost.",
  'dashboard.wallet.pricing.settlement': 'These figures are an estimate, not a quote. What you are actually charged settles on the token counts the engine reports for your session.',
  'dashboard.wallet.pricing.rateInputAudio': 'Input audio tokens',
  'dashboard.wallet.pricing.rateOutputText': 'Output text tokens',
  'dashboard.wallet.pricing.rateTtsInputText': 'Input text tokens',
  'dashboard.wallet.pricing.rateTtsOutputAudio': 'Output audio tokens',
  'dashboard.wallet.pricing.rateInputImage': 'Input image tokens (screen context)',
  'dashboard.wallet.pricing.perMillion': '{amount} / 1M tokens',
  'dashboard.wallet.pricing.cnyPerMillion': '¥{amount} / 1M tokens',
  'dashboard.wallet.pricing.fxNote': 'Converted at the ECB rate of {date}: 1 USD = {rate} CNY. The rate is refreshed daily.',
  'dashboard.wallet.pricing.sttReference': '1 hour of audio is ~30,000 input audio tokens • 1 hour of speech is ~15,000 output text tokens • 1 character of output is ~0.3 tokens',
  'dashboard.wallet.pricing.ttsReference': '1 character ≈ 0.3 input text tokens • 15,000 input text tokens ≈ 1 hour of generated speech • 1 hour of generated speech ≈ 30,000 output audio tokens',
  'dashboard.wallet.pricing.referenceNote': "Quoted from the provider. Translation produces a transcript and a translation, so an hour of speech produces about twice the output text tokens quoted above — that is the figure the table uses.",
  'dashboard.wallet.pricing.readOn': 'Provider prices read on {date}.',
  'dashboard.wallet.pricing.error': 'Could not load pricing',
  'dashboard.wallet.pricing.retry': 'Retry',
  'dashboard.wallet.pricing.boundariesHeading': 'What you are charged for',
  'dashboard.wallet.pricing.boundaryConnection': 'A connected stream is charged whether or not anyone is speaking. Each stream takes in about {count} input audio tokens an hour on its own; speaking adds output text on top of that.',
  'dashboard.wallet.pricing.boundaryReservation': 'Starting a session reserves enough balance to cover it at a cautious rate, which is what decides how long it may run. Your balance moves only on what you actually use, and the unused part of a reservation is never deducted.',
  'dashboard.wallet.pricing.boundaryCurrency': 'All figures are in US dollars.',
  'dashboard.wallet.pricing.faqHeading': 'Common questions',
  'dashboard.wallet.pricing.faqTranslationQ': 'Is translation charged separately?',
  'dashboard.wallet.pricing.faqTranslationA': 'No. Transcription and translation happen in the same stream, and translation is already included in the rates above.',
  'dashboard.wallet.pricing.faqFeaturesQ': 'Do speaker separation, language detection and formatting cost extra?',
  'dashboard.wallet.pricing.faqFeaturesA': 'No. Speaker separation, language identification and smart formatting are included at no extra charge.',
  'dashboard.billing.title': 'Billing',
  'dashboard.billing.subtitle': 'Your balance, credits and payments',
  'dashboard.usage.title': 'Usage',
  'dashboard.usage.subtitle': 'What your sessions have consumed',
  'dashboard.usage.sessionTime': 'of sessions',
  'dashboard.usage.sessions': 'sessions',
  'dashboard.usage.chartStart': '{{days}} days ago',
  'dashboard.usage.chartEnd': 'today',
  'dashboard.usage.chartLabel': 'Daily spend over the window',
  'dashboard.usage.noSpend': 'no usage',
  'dashboard.wallet.ledger.billingTitle': 'Billing history',
  'dashboard.wallet.ledger.viewReceipt': 'View receipt',
  'dashboard.wallet.lots.title': 'Credits',
  'dashboard.wallet.lots.totalRemaining': 'Available',
  'dashboard.wallet.lots.colCreated': 'Added',
  'dashboard.wallet.lots.colAmount': 'Amount',
  'dashboard.wallet.lots.colUsed': 'Used',
  'dashboard.wallet.lots.colRemaining': 'Remaining',
  'dashboard.wallet.lots.colExpires': 'Expires',
  'dashboard.wallet.lots.colStatus': 'Status',
  'dashboard.wallet.lots.status.active': 'Active',
  'dashboard.wallet.lots.status.expiring': 'Expiring soon',
  'dashboard.wallet.lots.status.spent': 'Used up',
  'dashboard.wallet.lots.status.expired': 'Expired',
  'dashboard.wallet.lots.empty': 'No credits yet.',
  'dashboard.wallet.lots.error': 'Could not load your credits.',
  'dashboard.wallet.lots.retry': 'Try again',
  'dashboard.wallet.paymentHistory': 'Payment History',
  'dashboard.wallet.noHistory': 'No payment history yet',
  'dashboard.wallet.loading': 'Loading wallet...',
  'dashboard.wallet.paymentSuccess': 'Payment successful! Your balance has been updated.',
  'dashboard.wallet.paymentUnavailable': 'Online payments are currently unavailable. Please try again later.',
  'dashboard.wallet.errorFetchingBalance': 'Failed to fetch wallet balance',
  'dashboard.wallet.minAmountError': 'Minimum amount is {min}',
  'dashboard.wallet.maxAmountError': 'Maximum amount is {max}',
  'dashboard.wallet.paymentError': 'Payment failed. Please try again.',

  // Dashboard Wallet — billing ledger
  'dashboard.wallet.ledger.title': 'Transaction history',
  'dashboard.wallet.ledger.kind.sttSpeaker': 'My speech recognition',
  'dashboard.wallet.ledger.kind.sttParticipant': "Other's speech recognition",
  'dashboard.wallet.ledger.kind.translateSpeaker': 'My speech translation',
  'dashboard.wallet.ledger.kind.translateParticipant': "Other's speech translation",
  'dashboard.wallet.ledger.kind.sttMixed': 'Combined speech recognition',
  'dashboard.wallet.ledger.kind.stt': 'Speech recognition',
  'dashboard.wallet.ledger.kind.tts': 'Speech synthesis',
  'dashboard.wallet.ledger.kind.ttsPreview': 'Voice preview',
  'dashboard.wallet.ledger.kind.topup': 'Top-up',
  'dashboard.wallet.ledger.kind.grant': 'Credit',
  'dashboard.wallet.ledger.kind.adjustment': 'Account adjustment',
  'dashboard.wallet.ledger.kind.refund': 'Refund',
  'dashboard.wallet.ledger.kind.expired': 'Expired balance',
  'dashboard.wallet.ledger.kind.usage': 'Usage',
  'dashboard.wallet.ledger.kind.voicePreview': 'Voice preview',
  'dashboard.wallet.ledger.group.session': 'Translation session',
  'dashboard.wallet.ledger.mode.me': 'Me',
  'dashboard.wallet.ledger.mode.other': 'Other',
  'dashboard.wallet.ledger.mode.both': 'Both',
  'dashboard.wallet.ledger.textOnly': 'Text Only',
  'dashboard.wallet.ledger.poweredBy': 'Powered by {name}',
  'dashboard.wallet.engine.qwenSingapore': 'Qwen (Singapore)',
  'dashboard.wallet.engine.qwenBeijing': 'Qwen (Beijing)',
  'dashboard.wallet.ledger.segments': 'segments',
  'dashboard.wallet.ledger.prevPage': 'Previous',
  'dashboard.wallet.ledger.nextPage': 'Next',
  'dashboard.wallet.ledger.prevPageAria': 'Previous page',
  'dashboard.wallet.ledger.nextPageAria': 'Next page',
  'dashboard.wallet.ledger.page': 'Page {page}',
  'dashboard.wallet.ledger.empty': 'No transactions yet',
  'dashboard.wallet.ledger.error': 'Could not load transactions',
  'dashboard.wallet.ledger.retry': 'Retry',
  'dashboard.wallet.ledger.expandRow': 'Show breakdown',
  'dashboard.wallet.ledger.collapseRow': 'Hide breakdown',

  // Dashboard Navigation - Wallet
  'dashboard.nav.wallet': 'Wallet',
  'dashboard.nav.billing': 'Billing',
  'dashboard.nav.usage': 'Usage',

  // Commerce Disclosure (特定商取引法に基づく表記)
  'commerce.title': 'Commerce Disclosure',
  'commerce.lastUpdated': 'Last Updated: October 6, 2026',

  // Operator Information
  'commerce.operator.title': 'Business Operator Information',
  'commerce.operator.name.label': 'Business Name',
  'commerce.operator.name.value': 'Kizuna AI Lab',
  'commerce.operator.representative.label': 'Operations Manager',
  'commerce.operator.representative.value': 'SHINONOME TAKU',
  'commerce.operator.address.label': 'Address',
  'commerce.operator.address.value': 'Will be disclosed upon request without delay',
  'commerce.operator.phone.label': 'Phone Number',
  'commerce.operator.phone.value': 'Will be disclosed upon request without delay (Please contact us via email)',
  'commerce.operator.email.label': 'Email Address',
  'commerce.operator.email.value': 'support@kizuna.ai',
  'commerce.operator.url.label': 'URL',
  'commerce.operator.url.value': 'https://sokuji.kizuna.ai',

  // Price
  'commerce.price.title': 'Pricing',
  'commerce.price.description': 'Pricing is as follows. All prices include applicable taxes.',
  'commerce.price.items': 'Prepaid balance is denominated in United States dollars (USD); the amount you pay is added to your balance|Translation is charged by actual usage as measured by the AI provider you select; estimated hourly rates are shown on your wallet page|Minimum purchase: $5 USD|Maximum purchase: $100 USD',
  'commerce.price.tax': 'For customers in Japan: Prices include consumption tax. For customers outside Japan: Local taxes may apply depending on your jurisdiction.',

  // Additional Fees
  'commerce.additionalFees.title': 'Additional Fees',
  'commerce.additionalFees.description': 'Other than the amount you pay to add balance, there are no additional fees for using this service.',

  // Payment
  'commerce.payment.title': 'Payment Methods',
  'commerce.payment.items': 'Credit cards|Debit cards|Apple Pay|Google Pay|Link|Alipay',
  'commerce.payment.note': 'Payments are processed through Stripe, Inc. Available payment methods may vary based on Stripe supported options. Alipay payments are charged in Japanese yen, converted from the US dollar amount at the European Central Bank reference rate shown before you pay.',

  // Payment Period
  'commerce.paymentPeriod.title': 'Payment Period',
  'commerce.paymentPeriod.content': 'Credit card and Alipay payments are processed immediately upon completing the purchase.',

  // Delivery
  'commerce.delivery.title': 'Service Delivery',
  'commerce.delivery.items': 'Your prepaid balance is credited to your account immediately upon payment completion|AI translation services are provided in real time; your balance is charged by actual usage once the provider reports it, shortly after a session ends|Your balance is valid for 1 year from the date of purchase',

  // Refund / Exchange Policy
  'commerce.refund.title': 'Returns and Exchanges',
  'commerce.refund.customerRequest.title': '<Customer-initiated Returns/Exchanges>',
  'commerce.refund.customerRequest.content': 'Due to the nature of digital services, returns or exchanges of prepaid balance after purchase are generally not accepted. However, for unused balance, refund requests can be submitted within 14 days of purchase. Processing fees may apply. To request a refund, please contact support@kizuna.ai with your purchase details and reason for refund.',
  'commerce.refund.defect.title': '<Defective Products/Service Issues>',
  'commerce.refund.defect.content': 'If the service was not provided properly due to technical issues, or if your balance was charged incorrectly, please contact support@kizuna.ai. After investigation, the affected amount will be credited to your account.',

  // Environment
  'commerce.environment.title': 'System Requirements',
  'commerce.environment.items': 'Browser extension: Chrome 116+, Edge 116+ (Chromium-based)|Desktop app: Windows 10+, macOS 13+, Linux (Ubuntu 20.04+ recommended)|Internet connection: Stable broadband connection required|Microphone: Audio input device required for voice translation',

  // Contact
  'commerce.contact.title': 'Contact Us',
  'commerce.contact.description': 'For questions or inquiries, please contact us through the following channels.',
  'commerce.contact.email': 'Email: support@kizuna.ai',
  'commerce.contact.github': 'GitHub: github.com/kizuna-ai-lab/sokuji',

  // Participant Audio Feature
  'nav.participantAudio': "Other's Audio",

  // Overview

  // How It Works

  // Platform Support

  // How to Enable

  // Platform Differences

  // Tips

  // FAQ

  // Device Selection Guide

  // Uninstall Feedback
  'uninstall.loading': 'Loading survey...',
  'uninstall.retry': 'Retry',
  'uninstall.fallbackTitle': "We're sorry to see you go",
  'uninstall.fallbackSubtitle': 'Please select all reasons that apply. Your feedback helps us improve.',
  'uninstall.otherPlaceholder': 'Please specify...',
  'uninstall.submitButton': 'Submit Feedback',
  'uninstall.tryDesktop': 'Try {link} — more features, works with any app',
  'uninstall.thankyouTitle': 'Thank you for your feedback!',
  'uninstall.thankyouSubtitle': 'We appreciate you taking the time to share your thoughts. Your feedback will help us improve Sokuji for future users.',
  'uninstall.reinstallPrompt': 'Changed your mind? You can always reinstall Sokuji.',
  'uninstall.reinstallButton': 'Reinstall from Chrome Web Store',
  'uninstall.needMoreFeatures': 'Need More Features?',
  'uninstall.needMoreFeaturesDesc': 'If you encountered unsupported websites or want custom API providers, try our desktop client!',
  'uninstall.tryDesktopClient': 'Try Sokuji Desktop Client',
  'uninstall.getInTouch': 'Get in Touch',
  'uninstall.reportBug': 'Report a bug:',
  'uninstall.shareIdea': 'Share an idea:',
  'uninstall.copyright': '© {year} Sokuji. All rights reserved.',

  // Subtitle Mode Feature
  'nav.subtitleMode': 'Subtitle Mode',
  'nav.basicFeatures': 'Basic Features',
  'nav.feature.translationModes': 'Translation Modes',
  'nav.feature.virtualMicrophone': 'Virtual Microphone',
  'nav.feature.textOnly': 'Spoken or Text Only',
  'nav.feature.display': 'Display Settings',
  'nav.feature.pronunciationReadings': 'Pronunciation Readings',
  'nav.feature.export': 'Export and Auto-Save',
  'nav.feature.replay': 'Replay',
  'nav.feature.noiseSuppression': 'Noise Suppression',
  'nav.feature.passthrough': 'Passthrough',
  'nav.feature.switchDevices': 'Switching Devices',
  'nav.feature.diagnosticLogs': 'Diagnostic Logs',
  'nav.feature.interfaceLanguage': 'Interface Language',
  'nav.feature.captionSharing': 'Caption Sharing',

  // Overview

  // How to enter and exit

  // Floating bar layout

  // Customization

  // Pin and Lock

  // Compact vs. expanded rows

  // Tips and limitations

  // FAQ

  // Admin console (FLAT keys — match the exact t('admin.*') calls in Phases 3/4/5)
  'admin.badge': 'Sokuji Admin',
  'admin.user.fallback': 'Admin',
  'admin.nav.wallet': 'Wallet',
  'admin.nav.users': 'Users',
  'admin.nav.signOut': 'Sign out',
  'admin.nav.backToDashboard': 'Back to dashboard',
  'admin.sidebar.collapse': 'Collapse sidebar',
  'admin.sidebar.expand': 'Expand sidebar',
  'admin.unauthorized.title': 'Not authorized',
  'admin.unauthorized.message': 'Your account does not have access to the admin console.',
  'admin.error.generic': 'Something went wrong. Please try again.',

  // Wallet — search
  'admin.wallet.searchTitle': 'Wallet management',
  'admin.wallet.searchSubtitle': 'Look up a user by email or ID to view and adjust their wallet.',
  'admin.wallet.searchLabel': 'Email or user ID',
  'admin.wallet.lookup': 'Look up',
  'admin.wallet.notFound': 'No user found for that email or ID.',
  'admin.wallet.loading': 'Loading…',
  'admin.wallet.backToSearch': '← Back to search',

  // Wallet — detail
  'admin.wallet.balance': 'Balance',
  'admin.wallet.last30Days': '30-day usage',
  'admin.wallet.recentLedger': 'Recent activity',
  'admin.wallet.topUpHistory': 'Top-up history',
  'admin.wallet.fullLedger': 'View full ledger →',

  // Wallet — adjust form
  'admin.wallet.adjustTitle': 'Credit / refund',
  'admin.wallet.action': 'Action',
  'admin.wallet.credit': 'Credit (add)',
  'admin.wallet.refund': 'Refund (subtract)',
  'admin.wallet.amountUsd': 'Amount in dollars (e.g. 12.34)',
  'admin.wallet.note': 'Note (optional)',
  'admin.wallet.apply': 'Apply',
  'admin.wallet.confirmTitle': 'Confirm adjustment',
  // {amount} is replaced with the resolved signed dollar amount, e.g. "+$20.00".
  'admin.wallet.confirmMessage': 'Apply {amount} to this wallet?',
  'admin.wallet.adjustSuccess': 'Balance updated',

  // Admin console — Wallet freeze/unfreeze (SP4)
  'admin.wallet.freeze': 'Freeze wallet',
  'admin.wallet.unfreeze': 'Unfreeze wallet',
  'admin.wallet.freezeConfirmTitle': 'Freeze this wallet?',
  'admin.wallet.unfreezeConfirmTitle': 'Unfreeze this wallet?',
  'admin.wallet.freezeConfirmMessage':
    'Freezing blocks new sessions and stops active ones on the next metered chunk (~30s).',
  'admin.wallet.unfreezeConfirmMessage':
    'Unfreezing lets this user start new relay sessions again.',
  'admin.wallet.freezeSuccess': 'Wallet frozen',
  'admin.wallet.unfreezeSuccess': 'Wallet unfrozen',

  // Ledger
  'admin.ledger.title': 'Ledger',
  'admin.ledger.backToWallet': '← Back to wallet',
  'admin.lots.title': 'Balance lots',
  'admin.lots.credited': 'Credited',
  'admin.lots.amount': 'Amount',
  'admin.lots.remaining': 'Remaining',
  'admin.lots.expires': 'Expires',
  'admin.lots.reminded': 'Reminded',
  'admin.lots.status': 'Status',
  'admin.lots.status.active': 'Active',
  'admin.lots.status.expiring': 'Expiring',
  'admin.lots.status.spent': 'Spent',
  'admin.lots.status.expired': 'Expired',
  'admin.lots.empty': 'No balance lots.',
  'admin.lots.inconsistent': 'This account’s lots do not match its ledger. Both daily expiry jobs skip it, so it is never warned and never swept — its balance will not expire until this is fixed.',
  'admin.ledger.when': 'When',
  'admin.ledger.type': 'Type',
  'admin.ledger.amount': 'Amount',
  'admin.ledger.ref': 'Reference',
  'admin.ledger.description': 'Description',
  'admin.ledger.empty': 'No ledger entries.',

  // Payments
  'admin.payments.credit': 'Credit (USD)',
  'admin.payments.usd': 'USD',
  'admin.payments.empty': 'No top-ups.',

  // Users — list
  'admin.users.title': 'Users',
  'admin.users.subtitle': 'Search users by email, name, or id.',
  'admin.users.searchLabel': 'Search',
  'admin.users.searchPlaceholder': 'email, name, or id',
  'admin.users.filterVerified': 'Email verified',
  'admin.users.filterPaid': 'Paid',
  'admin.users.filterAny': 'Any',
  'admin.users.verified': 'Verified',
  'admin.users.unverified': 'Unverified',
  'admin.users.anonymous': 'Anonymous',
  'admin.users.paid': 'Paid',
  'admin.users.unpaid': 'Never paid',
  'admin.users.sortLabel': 'Sort',
  'admin.users.sort.created_desc': 'Newest first',
  'admin.users.sort.created_asc': 'Oldest first',
  'admin.users.sort.email_asc': 'Email (A–Z)',
  'admin.users.sort.last_active_desc': 'Recently active first',
  'admin.users.sort.last_active_asc': 'Dormant first',
  'admin.users.sort.balance_desc': 'Highest balance first',
  'admin.users.sort.balance_asc': 'Lowest balance first',
  'admin.users.colEmail': 'Email',
  'admin.users.colName': 'Name',
  'admin.users.colStatus': 'Status',
  'admin.users.colBalance': 'Balance',
  'admin.users.colLastActive': 'Last active',
  'admin.users.colCreated': 'Created',
  'admin.users.empty': 'No users found.',
  'admin.users.view': 'View →',

  // Pagination
  'admin.pagination.prev': 'Prev',
  'admin.pagination.next': 'Next',
  'admin.pagination.prevAria': 'Previous page',
  'admin.pagination.nextAria': 'Next page',
  'admin.pagination.rows': 'Rows',
  'admin.pagination.page': 'Page',

  // Users — detail
  'admin.users.notFound': 'No user found for that id.',
  'admin.users.loading': 'Loading user…',
  'admin.users.backToList': '← Back to users',
  'admin.users.identity': 'Identity',
  'admin.users.manageWallet': 'Manage wallet →',
  'admin.users.fieldId': 'User id',
  'admin.users.fieldEmail': 'Email',
  'admin.users.fieldName': 'Name',
  'admin.users.fieldCreated': 'Created',
  'admin.users.fieldUpdated': 'Updated',
  'admin.users.providers': 'Linked providers',
  'admin.users.providersEmpty': 'No linked providers.',
  'admin.users.providerId': 'Provider',
  'admin.users.providerAccount': 'Account id',
  'admin.users.linkedAt': 'Linked',
  'admin.users.sessions': 'Recent sessions',
  'admin.users.sessionsEmpty': 'No recent sessions.',
  'admin.users.sessionStarted': 'Started',
  'admin.users.sessionExpires': 'Expires',
  'admin.users.sessionIp': 'IP',
  'admin.users.sessionLocation': 'Location',
  'admin.users.sessionDevice': 'Device',
  'admin.users.viewInPostHog': 'PostHog',

  // Admin console â Addresses (user_ips)
  'admin.users.addresses': 'Addresses',
  'admin.users.addressesWindow': 'Kept for {days} days after the last sighting, then pruned — this is not the account lifetime.',
  'admin.users.addressesEmpty': 'No addresses recorded for this user.',
  'admin.users.addressesTruncated': 'Showing the most recent addresses only — this user has more.',
  'admin.users.ipAddress': 'Address',
  'admin.users.ipFirstSeen': 'First seen',
  'admin.users.ipLastSeen': 'Last seen',
  'admin.users.ipSightings': 'Sightings',
  'admin.users.ipSightingsHelp': 'Sign-ins and session starts recorded at this address.',
  'admin.users.ipUserCount': 'Users',
  'admin.users.ipOthers': 'Other users here',
  'admin.users.ipNeighbourDeleted': 'deleted',
  'admin.users.ipNeighbourDeletedHelp': 'The account is gone; the sighting outlives it.',
  'admin.users.ipMoreUsers': '+{count} more',
  'admin.users.ipShowFewer': 'Show fewer',
  'admin.users.ipShowingOf': 'Showing {shown} of {total}.',

  // Admin console — Analytics (SP3)
  'admin.nav.analytics': 'Analytics',
  'admin.analytics.title': 'Analytics',
  'admin.analytics.subtitle': 'Revenue and usage across the platform.',
  'admin.analytics.posthogSignpost': 'User activity lives in PostHog',
  'admin.analytics.rangeLabel': 'Date range',
  'admin.analytics.rangeDays': '{days} days',
  'admin.analytics.kpiRevenue': 'Revenue',
  'admin.analytics.kpiUsage': 'Usage',
  'admin.analytics.kpiPaying': 'Paying users',
  'admin.analytics.kpiMargin': 'Margin',
  'admin.analytics.revenueChart': 'Revenue by day (USD)',
  'admin.analytics.usageChart': 'Usage by day (USD)',
  'admin.analytics.spenders24hTitle': 'Top spenders (last 24 hours)',
  'admin.analytics.spenders24hEmpty': 'No usage in the last 24 hours.',
  'admin.analytics.topUpsTitle': 'Recent top-ups',
  'admin.analytics.topUpsEmpty': 'No top-ups yet.',
  'admin.analytics.colUser': 'User',
  'admin.analytics.colUsage24h': '24h usage',
  'admin.analytics.colBalance': 'Balance',
  'admin.analytics.colAmount': 'Amount',
  'admin.analytics.colTime': 'Time (UTC)',
  'admin.analytics.colUserTopUps': 'Total top-ups',
  'admin.analytics.viewUser': 'View →',
  'admin.analytics.viewAll': 'View all →',
  'admin.analytics.backToAnalytics': 'Back to analytics',
  'admin.analytics.spendersListSubtitle': 'Every user with usage in the last 24 hours, highest first.',
  'admin.analytics.topUpsListTitle': 'All top-ups',
  'admin.analytics.topUpsListSubtitle': 'Every paid top-up, newest first. Free credit and admin adjustments are not top-ups.',

  // Admin console — Sessions (SP4)
  'admin.nav.sessions': 'Sessions',
  'admin.sessions.title': 'Sessions',
  'admin.sessions.subtitle': 'Live sessions from session leases, finished sessions from metered usage. Soniox is billed after a session ends, so a live row shows no usage yet.',
  'admin.sessions.windowLabel': 'Window',
  'admin.sessions.windowMinutes': '{minutes} min',
  'admin.sessions.refresh': 'Refresh',
  'admin.sessions.generatedAt': 'As of {time}',
  'admin.sessions.colUser': 'User',
  'admin.sessions.colProvider': 'Provider',
  'admin.sessions.colStreams': 'Streams',
  'admin.sessions.streamPending': 'Key issued, stream not connected yet',
  'admin.sessions.colUsage': 'Usage (USD)',
  'admin.sessions.colStarted': 'Started',
  'admin.sessions.colDuration': 'Duration',
  'admin.sessions.colStatus': 'Status',
  'admin.sessions.mode.text_only': 'Text Only',
  'admin.sessions.mode.speech_to_speech': 'Speech',
  'admin.sessions.mode.voice_preview': 'Voice preview',
  'admin.sessions.statusLive': 'Live',
  'admin.sessions.statusEnding': 'Ending',
  'admin.sessions.statusEnded': 'Ended',
  'admin.sessions.viewUser': 'View →',
  'admin.sessions.empty': 'No sessions in this window.',

  // Admin console — Qwen key pool
  'admin.nav.qwenKeys': 'Qwen keys',
  'admin.qwenKeys.title': 'Qwen keys',
  'admin.qwenKeys.subtitle': 'The managed Qwen LiveTranslate key pool: one key serves one session at a time, held for the token window.',
  'admin.qwenKeys.refresh': 'Refresh',
  'admin.qwenKeys.filterWorkspace': 'Workspace',
  'admin.qwenKeys.allWorkspaces': 'All workspaces',
  'admin.qwenKeys.sumWorkspaces': 'Workspaces',
  'admin.qwenKeys.sumActive': 'Active keys',
  'admin.qwenKeys.sumHeld': 'Held',
  'admin.qwenKeys.sumFree': 'Free',
  'admin.qwenKeys.sumSessions': 'Sessions this minute',
  'admin.qwenKeys.sumAlarms': 'Open alarms',
  'admin.qwenKeys.workspacesTitle': 'Workspaces',
  'admin.qwenKeys.keysTitle': 'Keys',
  'admin.qwenKeys.auditTitle': 'Audit, last 14 days',
  'admin.qwenKeys.auditEmpty': 'Nothing to flag.',
  'admin.qwenKeys.noWorkspaces': 'No workspaces yet.',
  'admin.qwenKeys.noKeys': 'No keys yet.',
  'admin.qwenKeys.addWorkspace': 'Add workspace',
  'admin.qwenKeys.addKey': 'Add key',
  'admin.qwenKeys.add': 'Add',
  'admin.qwenKeys.cancel': 'Cancel',
  'admin.qwenKeys.replace': 'Replace',
  'admin.qwenKeys.replaceSecret': 'Replace secret',
  'admin.qwenKeys.editLabel': 'Edit label',
  'admin.qwenKeys.save': 'Save',
  'admin.qwenKeys.pause': 'Pause',
  'admin.qwenKeys.resume': 'Resume',
  'admin.qwenKeys.paused': 'Paused',
  'admin.qwenKeys.resumed': 'Resumed',
  'admin.qwenKeys.colWorkspace': 'Workspace',
  'admin.qwenKeys.colRegion': 'Region',
  'admin.qwenKeys.regionSg': 'Singapore',
  'admin.qwenKeys.regionCn': 'Beijing',
  'admin.qwenKeys.colWorkspaceId': 'Alibaba id',
  'admin.qwenKeys.colStatus': 'Status',
  'admin.qwenKeys.colKeys': 'Keys',
  'admin.qwenKeys.keyCounts': '{keys} total, {held} held, {free} free',
  'admin.qwenKeys.colLogRead': 'Log read',
  'admin.qwenKeys.logFailing': 'Failing',
  'admin.qwenKeys.colKey': 'Key',
  'admin.qwenKeys.colState': 'State',
  'admin.qwenKeys.colHolder': 'Holder',
  'admin.qwenKeys.colLastIssued': 'Last issue',
  'admin.qwenKeys.colToday': 'Today (UTC+8)',
  'admin.qwenKeys.colAlarms': 'Alarms',
  'admin.qwenKeys.colAccount': 'Account',
  'admin.qwenKeys.colUnreported': 'Unreported',
  'admin.qwenKeys.colMismatched': 'Mismatched',
  'admin.qwenKeys.colIgnored': 'Report ignored',
  'admin.qwenKeys.stateActive': 'Active',
  'admin.qwenKeys.statePaused': 'Paused',
  'admin.qwenKeys.stateHeld': 'Held',
  'admin.qwenKeys.stateFree': 'Free',
  'admin.qwenKeys.pausedByHand': 'Paused by hand',
  'admin.qwenKeys.pausedRefused': 'Alibaba refused the secret',
  'admin.qwenKeys.pausedWorkspace': 'Workspace paused',
  'admin.qwenKeys.todaySessions': '{n} sessions',
  'admin.qwenKeys.tokensIssued': '{n} tokens issued',
  'admin.qwenKeys.speaks': 'speaks',
  'admin.qwenKeys.alarmUnclaimed': 'unclaimed',
  'admin.qwenKeys.alarmMismatch': 'mismatch',
  'admin.qwenKeys.alarmConflict': 'account conflict',
  'admin.qwenKeys.confirmPauseTitle': 'Pause this?',
  'admin.qwenKeys.confirmPauseWorkspace': 'No new session will be given a key from this workspace until it is resumed.',
  'admin.qwenKeys.confirmPauseKey': 'No new session will be given this key until it is resumed.',
  'admin.qwenKeys.fieldWorkspaceId': 'Alibaba workspace id',
  'admin.qwenKeys.fieldWorkspace': 'Workspace',
  'admin.qwenKeys.fieldRegion': 'Region',
  'admin.qwenKeys.regionHint': 'Which Alibaba site the workspace is in. It cannot be changed afterwards.',
  'admin.qwenKeys.chooseRegion': 'Choose a region',
  'admin.qwenKeys.fieldApikeyId': 'Key id (Alibaba apikey id)',
  'admin.qwenKeys.fieldLabel': 'Label',
  'admin.qwenKeys.fieldSecret': 'Secret',
  'admin.qwenKeys.fieldNote': 'Note (optional)',
  'admin.qwenKeys.secretHint': 'Checked with Alibaba, stored encrypted, never shown again.',
  'admin.qwenKeys.workspaceAdded': 'Workspace added',
  'admin.qwenKeys.keyAdded': 'Key added',
  'admin.qwenKeys.secretReplaced': 'Secret replaced',
  'admin.qwenKeys.labelUpdated': 'Label updated',
  'admin.qwenKeys.errWorkspaceNotFound': 'Alibaba has no such workspace, or it could not be listed.',
  'admin.qwenKeys.errWorkspaceUnreadable': 'The workspace was found but its model log cannot be read.',
  'admin.qwenKeys.errSecretRefused': 'Alibaba did not accept this secret.',
  'admin.qwenKeys.historyTitle': 'Key history',
  'admin.qwenKeys.windows': 'Windows',
  'admin.qwenKeys.noWindows': 'No windows yet.',
  'admin.qwenKeys.noSessions': 'No session logged.',
  'admin.qwenKeys.until': 'until',
  'admin.qwenKeys.loadMore': 'Load more',
  'admin.qwenKeys.unclaimed': 'Unclaimed rows',
  'admin.qwenKeys.events': 'Events',
  'admin.qwenKeys.reportIgnored': 'report ignored',
  'admin.qwenKeys.lease': 'lease',
  'admin.qwenKeys.usageTokens': 'in {audioIn} audio + {textIn} text, out {textOut} text + {audioOut} audio tokens',
  'admin.qwenKeys.noUsage': 'no usage',
  'admin.qwenKeys.noRateYet': '(no rate yet)',
  'admin.qwenKeys.colTenant': 'Tenant id',
  'admin.qwenKeys.logLag': 'lag {s}s',
  'admin.qwenKeys.neverRead': 'never read',
  'admin.qwenKeys.watermark': 'watermark',
  'admin.qwenKeys.leaseLink': 'Lease, see Sessions',
  // Local models (/docs/local-models)
  'localModels.title': 'Local Models and Licenses',
  'localModels.intro': 'Every model Sokuji can download for Free mode: what it does, how big it is, where it comes from, and the license its weights carry.',
  'localModels.runtimeTitle': 'Where models run',
  'localModels.runtimeBody': 'Models run in your browser, on the CPU through WebAssembly (WASM) or on the GPU through WebGPU. A WebGPU model needs a browser and graphics card that support WebGPU. Beyond that, how fast a model runs depends on your device. The size is the download, the same number Sokuji\'s model list shows, and a model needs at least that much memory to load.',
  'localModels.variantsNote': 'When a model comes in several variants, Sokuji picks one your device supports. Variants with f16 in their name need a GPU that supports 16-bit shaders.',
  'localModels.licenseTitle': 'Licenses',
  'localModels.licenseBody': 'Sokuji itself is free and open source under the AGPL-3.0. The models are not part of it: each comes from its publisher under its own license, listed below. Some allow only non-commercial use, and a few state no license at all. Using a model means following its publisher\'s terms.',
  'localModels.tier.open.label': 'Open license',
  'localModels.tier.open.desc': 'Permissive or attribution-only: MIT, Apache-2.0, BSD, CC0, CC BY or CC BY-SA.',
  'localModels.tier.conditional.label': 'Conditions apply',
  'localModels.tier.conditional.desc': 'Usable, with terms beyond attribution, such as a territory limit, a use policy or copyleft. The note says which.',
  'localModels.tier.non-commercial.label': 'Non-commercial',
  'localModels.tier.non-commercial.desc': 'The publisher allows non-commercial use only, or commercial use only on its own terms.',
  'localModels.tier.unstated.label': 'No license stated',
  'localModels.tier.unstated.desc': 'The publisher has not granted a license.',
  'localModels.tier.cloud.label': 'Online service',
  'localModels.tier.cloud.desc': 'Not a download: text is sent to an online service, so it needs an internet connection.',
  'localModels.flag.non-commercial-base': 'Non-commercial base voice',
  'localModels.flag.removed-upstream': 'Withdrawn by publisher',
  'localModels.flag.game-character': 'Game character voice',
  'localModels.flagNcBase': 'Non-commercial base voice: fine-tuned from a Piper voice (lessac or ryan) whose training data is licensed for non-commercial use only. The voice\'s own license does not say whether those terms carry over.',
  'localModels.type.asr': 'Speech recognition',
  'localModels.type.asr-stream': 'Streaming speech recognition',
  'localModels.type.translation': 'Translation',
  'localModels.type.tts': 'Speech synthesis',
  'localModels.type.punctuation': 'Punctuation',
  'localModels.runtime.wasm': 'CPU (WASM)',
  'localModels.runtime.webgpu': 'WebGPU',
  'localModels.runtime.cloud': 'Online',
  'localModels.col.model': 'Model',
  'localModels.col.languages': 'Languages',
  'localModels.col.runtime': 'Runs on',
  'localModels.col.size': 'Size',
  'localModels.col.source': 'Source',
  'localModels.col.license': 'License',
  'localModels.col.notes': 'Notes',
  'localModels.multilingual': 'Multilingual',
  'localModels.package': 'Package',
  'localModels.filterLabel': 'License',
  'localModels.search': 'Filter by name, language or license',
  'localModels.showing': '{count} models shown',
  'localModels.noMatch': 'No model matches the filter.',
  'localModels.bundled': 'Included in every offline speech recognition package:',
  'localModels.generatedFrom': 'Generated from Sokuji {version}.',

  // Account deletion page
  'deleteAccount.title': 'Delete your Sokuji account',
  'deleteAccount.intro': 'Sokuji is developed by Kizuna AI. You sign in to Sokuji on every platform, including the Android and iOS apps, with a Kizuna AI account. This page explains how to delete that account and what happens to your data. Deleting an account cannot be undone.',
  'deleteAccount.app.title': 'In the Sokuji mobile app',
  'deleteAccount.app.items': 'Open the menu on the main screen|Tap Account|Tap Delete account and confirm',
  'deleteAccount.web.title': 'On the website',
  'deleteAccount.web.items': 'Sign in at sokuji.kizuna.ai|Open the dashboard and go to Security|Select Delete My Account and type DELETE to confirm',
  'deleteAccount.email.title': 'If you cannot sign in',
  'deleteAccount.email.content': 'Email privacy@kizuna.ai from the address on your account and ask us to delete it.',
  'deleteAccount.deleted.title': 'What is deleted',
  'deleteAccount.deleted.items': 'Your account, including your email address, display name, password and sign-in records|Your remaining balance, which cannot be restored|Custom voices you created',
  'deleteAccount.partial.title': 'Delete some data without deleting your account',
  'deleteAccount.partial.items': 'Transcripts saved on your device: open History in the app and delete them|Usage analytics: email privacy@kizuna.ai and ask us to delete them',
};

export default en;
